import type { AxiosResponse } from 'axios';
import type { RegisteredEventInfo, PersonWrite, PersonWithRelations, PersonSortField, PersonSortDirection, DuplicateGroup, MergePersonsRequest, EmailCreate, EmailRead, EmailUpdate, PhoneCreate, PhoneRead, PhoneUpdate, Address, AddressWrite, AuthStatusResponse, AvailableMethodsResponse, RegisterRequest, AuthenticationResponse, MessageResponse, SessionListResponse, AccountInfo, UpdateAccountRequest, ChangePasswordRequest, SendVerificationRequest, VerifyEmailRequest, PasswordLoginRequest, ForgotPasswordRequest, ResetPasswordRequest, SSOInitiateRequest, SSOInitiateResponse, SSOCallbackRequest, SSOCallbackResponse, SSOLinkRequest, SSOLinkResponse, SSOUnlinkResponse, SSOLoginRequest, IdentityAdminUpdate, MfaStatusResponse, TotpSetupResponse, TotpConfirmRequest, DisableMfaRequest, MfaSendRequest, MfaVerifyRequest, InvitationResponse, InvitationCreate, InvitationStatus, BloxTemplateRead, SiteBlueprint, BlueprintPlan, BlueprintApplyResult, WebsiteRead, WebsiteCreate, PublicWebsiteRef, RepositoryValidationRequest, RepositoryValidationRead, DeploymentProvisionRequest, ProvisionResult, DeploymentRead, WebsiteUpdate, BlockManifest, PageCreate, PageRead, PageUpdate, PageVersionRead, PublishResult, GlobalBlockRead, GlobalBlockUpsert, PageSchemaRead, PageResolveRead, RedirectCreate, RedirectRead, RedirectUpdate, PlausibleRealtimeVisitors, PlausibleAggregateMetrics, PlausibleTimeseriesRead, PlausibleBreakdownRead, BuildBundleRead, BuildCollectionRead, ManifestUpload, StoreCatalogRead, CollectionSchemaCreate, CollectionSchemaRead, CollectionSchemaUpdate, RenameFieldRequest, RenameFieldResult, CollectionItemCreate, CollectionItemRead, CollectionItemUpdate, MoveRequest, FormSubmissionCreate, FormSubmissionRead, FormSubStatus, FormSubmissionUpdate, FormStepUpdate, FormDefinitionCreate, FormDefinitionRead, FormDefinitionUpdate, FormDefinitionRevisionRead, FormDefinitionLocaleRead, FormDefinitionLocaleWrite, FormDispatchRequest, FormDeliveryReceipt, ResolveNextRequest, ResolveNextResponse, ValidateFormRequest, ValidateFormResponse, EmailDocumentRenderRequest, EmailDocumentRenderResponse, EmailTemplateCreate, EmailTemplateResponse, TemplateType, EmailTemplateUpdate, EmailSuppressionResponse, SuppressEmailRequest, CampaignCreate, CampaignResponse, CampaignStatus, CampaignUpdate, CampaignStatsResponse, SendTestEmailRequest, SendTestEmailResponse, UnsubscribeRequest, GrantTokenRequest, FileGrantToken, FitMode, OutputFormat, FileResponse, AttachRequest, FileAttachmentRead, AttachManyRequest, AutomationPalette, AutomationResponse, AutomationCreate, AutomationInvokeRequest, AutomationExecutionResponse, BuildAutomationRequest, BuilderResponse, AutomationUpdate, TestAutomationRequest, TestAutomationResult, OperatorInfo, AutomationStats, ToolsListResponse, ToolsDisplayResponse, AgentPublic, ModelInfo, SkillCreate, SkillResponse, SkillUpdate, ArtifactResponse, ArtifactRename, ArtifactSourceUpdate, ConversationCreate, AgentConversationResponse, ConversationUpdate, QueueListResponse, UpdateQueueEntryRequest, QueueEntryResponse, ConversationHistoryResponse, SendAgentMessageRequest, StartMessageRunResponse, CompressContextRequest, CompressContextResponse, PendingInterrupt, ResumeRunRequest, AgentMessageResponse, FeedbackCreate, AgentFeedbackRead, FeedbackSource, FeedbackLayer, FeedbackStatus, FeedbackAggregateRow, FeedbackUpdate, FeedbackReproResult, JudgeAttributeResult, JudgeScanResult, CapabilityConnectorCreate, CapabilityConnectorResponse, CapabilityItem, CapabilityDetail, CapabilityConnectorUpdate, OAuthAuthorizeResponse, MemoryCreate, MemoryResponse, MemoryUpdate, ScenarioCreate, ScenarioResponse, ScenarioUpdate, ScenarioMessageData, ScenarioMessageUpdate, AgentCreate, AgentResponse, AgentStatus, AgentUpdate, AgentStarterPromptResponse, AgentCapabilitiesUpdate } from './types.d';
import {
	ApiResponse,
	createApiTransport,
	configureApiTransport,
	parseApiError,
	formatFieldErrors,
	useApiRequest,
	useCancellableRequest,
	unwrap,
	type StreamController, type SSEStreamOptions,
	type ApiConfig,
	type ParsedError,
	type FastAPIValidationError,
	type FastAPIErrorResponse,
	type ResponseMetadata,
	type ApiRequestState,
	type RequestInterceptor,
	type ResponseInterceptor,
	type ApiTransport,
	type Unwrapped
} from '@bagelink/sdk';
import type { Ref } from 'vue';

/**
 * Options for file upload operations
 */
export interface UploadOptions {
	/** Progress callback for upload operations */
	onUploadProgress?: (progressEvent: any) => void
	/** Directory path for the uploaded file */
	dirPath?: string
	/** Tags to associate with the uploaded file */
	tags?: string[]
}


// ============================================================================
// Stream Event Type Definitions (Fully Typed!)
// ============================================================================

/**
 * Event types for AgentsConversationsMessagesStream stream
 * Events: "done", "error", "middleware", "reasoning", "skill_activated", "token", "tool_call", "tool_result", "user_message_saved"
 *
 * - **user_message_saved**: Event data
 * - **token**: Event data
 * - **done**: Event data
 */
export interface AgentsConversationsMessagesStreamStreamEvents {
  /**
   * user_message_saved
   * - `message_id`: Field data
   */
  user_message_saved: {
    /** message_id */
    message_id: any
  }
  /**
   * token
   * - `delta`: Field data
   * - `message_id`: Field data
   * - `role`: Field data
   */
  token: {
    /** delta */
    delta: any
    /** message_id */
    message_id: any
    /** role */
    role: any
  }
  /**
   * done
   * - `reason`: Field data
   * - `message_id`: Field data
   * - `usage`: Field data
   */
  done: {
    /** reason */
    reason: any
    /** message_id */
    message_id: any
    /** usage */
    usage: any
  }
}



const defaultTransport = createApiTransport({ baseURL: '/api' })

function createEndpointNamespaces(__transport: ApiTransport) {

/**
 * API functions for events endpoints
 */
const events = /**
 * List Available Events
 *
 * List all available events that can be emitted.
 * 
 * Shows events registered via create_emitter() including:
 * - Event name
 * - Description
 * - Source module
 * - Example payload
 * - Payload JSON Schema
 * - Number of subscribers
 *
 * @endpoint GET /events
 */
async (): Promise<ApiResponse<RegisteredEventInfo[]>> => {
        const response = await __transport.axios.get('/events')
        return ApiResponse.create(response.data, response)};

/**
 * API functions for person endpoints
 */
const person = {
  getByPersonId: /**
 * Get Person
 *
 * Get a person by ID
 *
 * @endpoint GET /person/{person_id}
 */
async ({ personId }: { personId?: string } = {}): Promise<PersonWithRelations> => {
        const response = await __transport.axios.get(`/person/${personId}`)
        return ApiResponse.create(response.data, response)},
  put: /**
 * Update Person
 *
 * Update a person's SCALAR details only.
 * 
 * Nested emails/phones/addresses are IGNORED here — contacts are independent
 * sub-resources mutated via the granular /person/{id}/{email,phone} endpoints.
 * Nesting is only unambiguous on create; on update it caused the
 * delete-then-PUT-re-creates-with-new-id bug. We strip them and log a
 * deprecation warning (backward-compat). The response still returns current
 * contacts so the client can re-sync.
 *
 * @endpoint PUT /person/{person_id}
 */
async ({ personId, personWrite }: { personId?: string, personWrite?: PersonWrite } = {}): Promise<PersonWithRelations> => {
        const response = await __transport.axios.put(`/person/${personId}`, personWrite)
        return ApiResponse.create(response.data, response)},
  delete: /**
 * Delete Person
 *
 * Delete a person
 *
 * @endpoint DELETE /person/{person_id}
 */
async ({ personId }: { personId?: string } = {}): Promise<{ [key: string]: any }> => {
        const response = await __transport.axios.delete(`/person/${personId}`)
        return response.data},
  post: /**
 * Create New Person
 *
 * Create a person with full contact and address information
 *
 * @endpoint POST /person
 */
async ({ personWrite }: { personWrite?: PersonWrite } = {}): Promise<PersonWithRelations> => {
        const response = await __transport.axios.post('/person', personWrite)
        return ApiResponse.create(response.data, response)},
  get: /**
 * List Persons
 *
 * List/search persons with flexible filtering options.
 * 
 * Two search modes:
 * 
 * 1. Fuzzy Search (search parameter):
 *    - GET /person?search=john
 *    - Searches across all fields with relevance scoring
 *    - User-friendly for quick lookups
 * 
 * 2. Structured Filter (q parameter):
 *    - GET /person?q=first_name eq "John" and age gt 25
 *    - Precise SCIM 2.0 queries for any field
 *    - Supports relationships: emails.value, phones.value
 *    - Supports JSON arrays: roles has "Student"
 * 
 * Examples:
 *    - GET /person?search=john (fuzzy search)
 *    - GET /person?q=first_name eq "John" (structured filter)
 *    - GET /person?q=national_id eq "123456789" (find by national ID)
 *    - GET /person?q=roles has "Student" (filter by role - JSON array!)
 *    - GET /person?q=roles has "Student" and age gt 18 (combine filters)
 *    - GET /person?q=emails.value co "@example.com" (filter by email domain)
 *
 * @endpoint GET /person
 */
async ({ search, offset, limit = 100, _includeArchived, _sortBy, _sortDirection = 'desc', q }: { search?: string, offset?: number, limit?: number, _includeArchived?: boolean, _sortBy?: PersonSortField, _sortDirection?: PersonSortDirection, q?: string } = {}): Promise<ApiResponse<PersonWithRelations[]>> => {
        const response = await __transport.axios.get('/person', { params: {search, offset, limit, 'include_archived': _includeArchived, 'sort_by': _sortBy, 'sort_direction': _sortDirection, q} })
        return ApiResponse.create(response.data, response)},
  findDuplicates: /**
 * Find Duplicates
 *
 * Find potential duplicate persons based on:
 * - Same email addresses
 * - Same phone numbers
 * - Similar names (exact first+last name match)
 * - Same national ID
 * 
 * Returns groups of duplicates with the oldest person as the primary.
 *
 * @endpoint GET /person/find-duplicates
 */
async ({ offset, limit = 100 }: { offset?: number, limit?: number } = {}): Promise<ApiResponse<DuplicateGroup[]>> => {
        const response = await __transport.axios.get('/person/find-duplicates', { params: {offset, limit} })
        return ApiResponse.create(response.data, response)},
  merge: /**
 * Merge Persons Route
 *
 * Merge duplicate persons into a primary person.
 * 
 * This endpoint:
 * - Updates all foreign key references from duplicates to primary
 * - Transfers all emails, phones, and addresses from duplicates to primary
 * - Merges roles from all persons
 * - Archives duplicate persons and marks them with merged_into_id
 * 
 * Note: Duplicates are archived rather than deleted to preserve audit trails.
 *
 * @endpoint POST /person/merge
 */
async ({ mergePersonsRequest }: { mergePersonsRequest?: MergePersonsRequest } = {}): Promise<PersonWithRelations> => {
        const response = await __transport.axios.post('/person/merge', mergePersonsRequest)
        return ApiResponse.create(response.data, response)},
  archive: /**
 * Archive Person
 *
 * Archive a person
 *
 * @endpoint PUT /person/{person_id}/archive
 */
async ({ personId }: { personId?: string } = {}): Promise<PersonWithRelations> => {
        const response = await __transport.axios.put(`/person/${personId}/archive`, {})
        return ApiResponse.create(response.data, response)},
  unarchive: /**
 * Unarchive Person
 *
 * Unarchive a person
 *
 * @endpoint PUT /person/{person_id}/unarchive
 */
async ({ personId }: { personId?: string } = {}): Promise<PersonWithRelations> => {
        const response = await __transport.axios.put(`/person/${personId}/unarchive`, {})
        return ApiResponse.create(response.data, response)},
  email: {
    patch: /**
 * Update Email
 *
 * Patch an email. Changing the value resets verification.
 *
 * @endpoint PATCH /person/email/{email_id}
 */
async ({ emailId, emailUpdate }: { emailId?: string, emailUpdate?: EmailUpdate } = {}): Promise<EmailRead> => {
        const response = await __transport.axios.patch(`/person/email/${emailId}`, emailUpdate)
        return ApiResponse.create(response.data, response)},
    delete: /**
 * Delete Email
 *
 * Delete an email. Idempotent — deleting an already-gone id still 204s.
 *
 * @endpoint DELETE /person/email/{email_id}
 */
async ({ emailId }: { emailId?: string } = {}) => {
        const response = await __transport.axios.delete(`/person/email/${emailId}`)
        return ApiResponse.create(response.data, response)},
    post: /**
 * Create Email
 *
 * Create an email for a person (404 if the person doesn't exist).
 *
 * @endpoint POST /person/{person_id}/email
 */
async ({ personId, emailCreate }: { personId?: string, emailCreate?: EmailCreate } = {}): Promise<EmailRead> => {
        const response = await __transport.axios.post(`/person/${personId}/email`, emailCreate)
        return ApiResponse.create(response.data, response)},
    primary: /**
 * Set Primary Email Route
 *
 * Mark an email primary (unsets the person's other primary emails).
 *
 * @endpoint POST /person/email/{email_id}/primary
 */
async ({ emailId }: { emailId?: string } = {}): Promise<EmailRead> => {
        const response = await __transport.axios.post(`/person/email/${emailId}/primary`, {})
        return ApiResponse.create(response.data, response)}
  },
  phone: {
    patch: /**
 * Update Phone
 *
 * Patch a phone. Changing the value resets verification.
 *
 * @endpoint PATCH /person/phone/{phone_id}
 */
async ({ phoneId, phoneUpdate }: { phoneId?: string, phoneUpdate?: PhoneUpdate } = {}): Promise<PhoneRead> => {
        const response = await __transport.axios.patch(`/person/phone/${phoneId}`, phoneUpdate)
        return ApiResponse.create(response.data, response)},
    delete: /**
 * Delete Phone
 *
 * Delete a phone. Idempotent — deleting an already-gone id still 204s.
 *
 * @endpoint DELETE /person/phone/{phone_id}
 */
async ({ phoneId }: { phoneId?: string } = {}) => {
        const response = await __transport.axios.delete(`/person/phone/${phoneId}`)
        return ApiResponse.create(response.data, response)},
    post: /**
 * Create Phone
 *
 * Create a phone for a person (404 if the person doesn't exist).
 *
 * @endpoint POST /person/{person_id}/phone
 */
async ({ personId, phoneCreate }: { personId?: string, phoneCreate?: PhoneCreate } = {}): Promise<PhoneRead> => {
        const response = await __transport.axios.post(`/person/${personId}/phone`, phoneCreate)
        return ApiResponse.create(response.data, response)},
    primary: /**
 * Set Primary Phone Route
 *
 * Mark a phone primary (unsets the person's other primary phones).
 *
 * @endpoint POST /person/phone/{phone_id}/primary
 */
async ({ phoneId }: { phoneId?: string } = {}): Promise<PhoneRead> => {
        const response = await __transport.axios.post(`/person/phone/${phoneId}/primary`, {})
        return ApiResponse.create(response.data, response)}
  },
  address: {
    put: /**
 * Update Address Route
 *
 * Update an address
 *
 * @endpoint PUT /person/address/{address_id}
 */
async ({ addressId, addressWrite }: { addressId?: string, addressWrite?: AddressWrite } = {}): Promise<Address> => {
        const response = await __transport.axios.put(`/person/address/${addressId}`, addressWrite)
        return ApiResponse.create(response.data, response)},
    delete: /**
 * Delete Address
 *
 * @endpoint DELETE /person/address/{address_id}
 */
async ({ addressId }: { addressId?: string } = {}): Promise<{ [key: string]: any }> => {
        const response = await __transport.axios.delete(`/person/address/${addressId}`)
        return response.data},
    post: /**
 * Add Address
 *
 * Add an address to a person
 *
 * @endpoint POST /person/{person_id}/address
 */
async ({ personId, address }: { personId?: string, address?: Address } = {}): Promise<Address> => {
        const response = await __transport.axios.post(`/person/${personId}/address`, address)
        return ApiResponse.create(response.data, response)}
  }
};

/**
 * API functions for authentication endpoints
 */
const authentication = {
  status: /**
 * Auth Status
 *
 * Get authentication status and available methods
 *
 * @endpoint GET /authentication/status
 */
async (): Promise<AuthStatusResponse> => {
        const response = await __transport.axios.get('/authentication/status')
        return ApiResponse.create(response.data, response)},
  methods: /**
 * Get Available Methods
 *
 * Get available authentication methods for this instance
 *
 * @endpoint GET /authentication/methods
 */
async (): Promise<AvailableMethodsResponse> => {
        const response = await __transport.axios.get('/authentication/methods')
        return ApiResponse.create(response.data, response)},
  register: /**
 * Register
 *
 * Register a new identity with authentication methods
 *
 * @endpoint POST /authentication/register
 */
async ({ registerRequest }: { registerRequest?: RegisterRequest } = {}): Promise<AuthenticationResponse> => {
        const response = await __transport.axios.post('/authentication/register', registerRequest)
        return ApiResponse.create(response.data, response)},
  logout: /**
 * Logout
 *
 * Logout and clear session
 *
 * @endpoint POST /authentication/logout
 */
async (): Promise<MessageResponse> => {
        const response = await __transport.axios.post('/authentication/logout', {})
        return ApiResponse.create(response.data, response)},
  sessions: {
    deleteBySessionToken: /**
 * Revoke Session
 *
 * Revoke one of the caller's own sessions.
 *
 * @endpoint DELETE /authentication/sessions/{session_token}
 */
async ({ sessionToken }: { sessionToken?: string } = {}): Promise<MessageResponse> => {
        const response = await __transport.axios.delete(`/authentication/sessions/${sessionToken}`)
        return ApiResponse.create(response.data, response)},
    get: /**
 * Get My Sessions
 *
 * Get active sessions for current identity
 *
 * @endpoint GET /authentication/sessions
 */
async (): Promise<SessionListResponse> => {
        const response = await __transport.axios.get('/authentication/sessions')
        return ApiResponse.create(response.data, response)},
    delete: /**
 * Revoke All My Sessions
 *
 * Revoke all sessions for current identity
 *
 * @endpoint DELETE /authentication/sessions
 */
async (): Promise<MessageResponse> => {
        const response = await __transport.axios.delete('/authentication/sessions')
        return ApiResponse.create(response.data, response)}
  },
  cleanupSessions: /**
 * Cleanup Expired Sessions
 *
 * Clean up expired sessions (admin endpoint)
 *
 * @endpoint POST /authentication/cleanup-sessions
 */
async (): Promise<MessageResponse> => {
        const response = await __transport.axios.post('/authentication/cleanup-sessions', {})
        return ApiResponse.create(response.data, response)},
  me: {
    get: /**
 * Get Current User
 *
 * Get current authenticated identity info
 *
 * @endpoint GET /authentication/me
 */
async (): Promise<AccountInfo> => {
        const response = await __transport.axios.get('/authentication/me')
        return ApiResponse.create(response.data, response)},
    delete: /**
 * Delete Current User
 *
 * Delete current identity (soft delete - deactivates)
 *
 * @endpoint DELETE /authentication/me
 */
async (): Promise<MessageResponse> => {
        const response = await __transport.axios.delete('/authentication/me')
        return ApiResponse.create(response.data, response)},
    patch: /**
 * Update Current User
 *
 * Update current identity's display name
 *
 * @endpoint PATCH /authentication/me
 */
async ({ updateAccountRequest }: { updateAccountRequest?: UpdateAccountRequest } = {}): Promise<AccountInfo> => {
        const response = await __transport.axios.patch('/authentication/me', updateAccountRequest)
        return ApiResponse.create(response.data, response)}
  },
  password: {
    change: /**
 * Change Password
 *
 * Change password for current user (requires current password)
 *
 * @endpoint POST /authentication/password/change
 */
async ({ changePasswordRequest }: { changePasswordRequest?: ChangePasswordRequest } = {}): Promise<MessageResponse> => {
        const response = await __transport.axios.post('/authentication/password/change', changePasswordRequest)
        return ApiResponse.create(response.data, response)},
    forgot: /**
 * Forgot Password
 *
 * Initiate password reset flow
 *
 * @endpoint POST /authentication/password/forgot
 */
async ({ forgotPasswordRequest }: { forgotPasswordRequest?: ForgotPasswordRequest } = {}): Promise<MessageResponse> => {
        const response = await __transport.axios.post('/authentication/password/forgot', forgotPasswordRequest)
        return ApiResponse.create(response.data, response)},
    verifyResetToken: /**
 * Verify Reset Token
 *
 * Verify if password reset token is valid
 *
 * @endpoint GET /authentication/password/verify-reset-token/{token}
 */
async ({ token }: { token?: string } = {}): Promise<MessageResponse> => {
        const response = await __transport.axios.get(`/authentication/password/verify-reset-token/${token}`)
        return ApiResponse.create(response.data, response)},
    reset: /**
 * Reset Password
 *
 * Reset password with token
 *
 * @endpoint POST /authentication/password/reset
 */
async ({ resetPasswordRequest }: { resetPasswordRequest?: ResetPasswordRequest } = {}): Promise<MessageResponse> => {
        const response = await __transport.axios.post('/authentication/password/reset', resetPasswordRequest)
        return ApiResponse.create(response.data, response)}
  },
  verify: {
    send: /**
 * Send Email Verification
 *
 * Send email verification link
 *
 * @endpoint POST /authentication/verify/send
 */
async ({ identity, sendVerificationRequest }: { identity?: any, sendVerificationRequest?: SendVerificationRequest } = {}): Promise<MessageResponse> => {
        const response = await __transport.axios.post('/authentication/verify/send', sendVerificationRequest, { params: {identity} })
        return ApiResponse.create(response.data, response)},
    email: /**
 * Verify Email
 *
 * Verify email with token
 *
 * @endpoint POST /authentication/verify/email
 */
async ({ verifyEmailRequest }: { verifyEmailRequest?: VerifyEmailRequest } = {}): Promise<MessageResponse> => {
        const response = await __transport.axios.post('/authentication/verify/email', verifyEmailRequest)
        return ApiResponse.create(response.data, response)}
  },
  refresh: /**
 * Refresh Session
 *
 * Refresh current session and extend expiration
 *
 * @endpoint POST /authentication/refresh
 */
async (): Promise<AuthenticationResponse> => {
        const response = await __transport.axios.post('/authentication/refresh', {})
        return ApiResponse.create(response.data, response)},
  login: {
    password: /**
 * Login Password
 *
 * Login with email and password
 *
 * @endpoint POST /authentication/login/password
 */
async ({ passwordLoginRequest }: { passwordLoginRequest?: PasswordLoginRequest } = {}): Promise<AuthenticationResponse> => {
        const response = await __transport.axios.post('/authentication/login/password', passwordLoginRequest)
        return ApiResponse.create(response.data, response)},
    sso: {
      google: /**
 * Login Sso
 *
 * Login with this SSO provider (legacy endpoint without PKCE)
 *
 * @endpoint POST /authentication/login/sso/google
 */
async ({ sSOLoginRequest }: { sSOLoginRequest?: SSOLoginRequest } = {}): Promise<AuthenticationResponse> => {
        const response = await __transport.axios.post('/authentication/login/sso/google', sSOLoginRequest)
        return ApiResponse.create(response.data, response)}
    }
  },
  sso: {
    google: {
      initiate: /**
 * Initiate Sso
 *
 * Initiate SSO flow with PKCE
 *
 * @endpoint POST /authentication/sso/google/initiate
 */
async ({ sSOInitiateRequest }: { sSOInitiateRequest?: SSOInitiateRequest } = {}): Promise<SSOInitiateResponse> => {
        const response = await __transport.axios.post('/authentication/sso/google/initiate', sSOInitiateRequest)
        return ApiResponse.create(response.data, response)},
      callback: /**
 * Sso Callback
 *
 * Handle SSO callback with PKCE verification
 *
 * @endpoint POST /authentication/sso/google/callback
 */
async ({ sSOCallbackRequest }: { sSOCallbackRequest?: SSOCallbackRequest } = {}): Promise<SSOCallbackResponse> => {
        const response = await __transport.axios.post('/authentication/sso/google/callback', sSOCallbackRequest)
        return ApiResponse.create(response.data, response)},
      link: /**
 * Link Sso Provider
 *
 * Link SSO provider to existing account
 *
 * @endpoint POST /authentication/sso/google/link
 */
async ({ sSOLinkRequest }: { sSOLinkRequest?: SSOLinkRequest } = {}): Promise<SSOLinkResponse> => {
        const response = await __transport.axios.post('/authentication/sso/google/link', sSOLinkRequest)
        return ApiResponse.create(response.data, response)},
      unlink: /**
 * Unlink Sso Provider
 *
 * Unlink SSO provider from account
 *
 * @endpoint DELETE /authentication/sso/google/unlink
 */
async (): Promise<SSOUnlinkResponse> => {
        const response = await __transport.axios.delete('/authentication/sso/google/unlink')
        return ApiResponse.create(response.data, response)}
    }
  },
  mfa: {
    status: /**
 * Mfa Status
 *
 * Report MFA state and enrolled methods for the current identity.
 *
 * @endpoint GET /authentication/mfa/status
 */
async (): Promise<MfaStatusResponse> => {
        const response = await __transport.axios.get('/authentication/mfa/status')
        return ApiResponse.create(response.data, response)},
    totp: {
      setup: /**
 * Totp Setup
 *
 * Begin TOTP enrollment; returns a one-time secret and otpauth URI.
 *
 * @endpoint POST /authentication/mfa/totp/setup
 */
async (): Promise<TotpSetupResponse> => {
        const response = await __transport.axios.post('/authentication/mfa/totp/setup', {})
        return ApiResponse.create(response.data, response)},
      confirm: /**
 * Totp Confirm
 *
 * Verify the first TOTP code and enable MFA.
 *
 * @endpoint POST /authentication/mfa/totp/confirm
 */
async ({ totpConfirmRequest }: { totpConfirmRequest?: TotpConfirmRequest } = {}): Promise<AuthenticationResponse> => {
        const response = await __transport.axios.post('/authentication/mfa/totp/confirm', totpConfirmRequest)
        return ApiResponse.create(response.data, response)}
    },
    disable: /**
 * Mfa Disable
 *
 * Disable MFA (requires a current second-factor code).
 *
 * @endpoint POST /authentication/mfa/disable
 */
async ({ disableMfaRequest }: { disableMfaRequest?: DisableMfaRequest } = {}): Promise<AuthenticationResponse> => {
        const response = await __transport.axios.post('/authentication/mfa/disable', disableMfaRequest)
        return ApiResponse.create(response.data, response)},
    send: /**
 * Mfa Send
 *
 * Send an email/SMS step-up code for a pending challenge.
 *
 * @endpoint POST /authentication/mfa/send
 */
async ({ mfaSendRequest }: { mfaSendRequest?: MfaSendRequest } = {}): Promise<AuthenticationResponse> => {
        const response = await __transport.axios.post('/authentication/mfa/send', mfaSendRequest)
        return ApiResponse.create(response.data, response)},
    verify: /**
 * Mfa Verify
 *
 * Verify the second factor and issue a session.
 *
 * @endpoint POST /authentication/mfa/verify
 */
async ({ mfaVerifyRequest }: { mfaVerifyRequest?: MfaVerifyRequest } = {}): Promise<AuthenticationResponse> => {
        const response = await __transport.axios.post('/authentication/mfa/verify', mfaVerifyRequest)
        return ApiResponse.create(response.data, response)}
  }
};

/**
 * API functions for admin endpoints
 */
const admin = {
  identities: {
    getByIdentityId: /**
 * Get Identity
 *
 * Get a single identity with their linked person.
 *
 * @endpoint GET /admin/identities/{identity_id}
 */
async ({ identityId }: { identityId?: string } = {}): Promise<AccountInfo> => {
        const response = await __transport.axios.get(`/admin/identities/${identityId}`)
        return ApiResponse.create(response.data, response)},
    patch: /**
 * Update Identity
 *
 * Update roles, active status, or unlock an identity.
 *
 * @endpoint PATCH /admin/identities/{identity_id}
 */
async ({ identityId, identityAdminUpdate }: { identityId?: string, identityAdminUpdate?: IdentityAdminUpdate } = {}): Promise<AccountInfo> => {
        const response = await __transport.axios.patch(`/admin/identities/${identityId}`, identityAdminUpdate)
        return ApiResponse.create(response.data, response)},
    get: /**
 * List Identities
 *
 * List all identities with their linked person.
 * 
 * When ``_tenantId`` is provided, only identities with an active
 * TenantMembership in that tenant are returned.
 * 
 * Supports ``?search=`` for free-text keyword search across email,
 * display name, and person name, and ``?q=`` for SCIM 2.0 query filters
 * on Identity fields.
 *
 * @endpoint GET /admin/identities
 */
async ({ offset, limit = 100, _includeInactive, _tenantId, search, q }: { offset?: number, limit?: number, _includeInactive?: boolean, _tenantId?: string, search?: string, q?: string } = {}): Promise<ApiResponse<AccountInfo[]>> => {
        const response = await __transport.axios.get('/admin/identities', { params: {offset, limit, 'include_inactive': _includeInactive, '_tenantId': _tenantId, search, q} })
        return ApiResponse.create(response.data, response)}
  }
};

/**
 * API functions for invitations endpoints
 */
const invitations = {
  delete: /**
 * Delete Invitation
 *
 * Delete invitation permanently.
 *
 * @endpoint DELETE /invitations/{invitation_id}
 */
async ({ invitationId }: { invitationId?: string } = {}) => {
        const response = await __transport.axios.delete(`/invitations/${invitationId}`)
        return ApiResponse.create(response.data, response)},
  post: /**
 * Create Invitation
 *
 * Create invitation.
 * 
 * Tenant scoping comes from ``tenant_id`` in the request body
 * (platform_admin flow). Tenant-member invitations (via X-Tenant-ID
 * header) will be added when that flow is integrated.
 * 
 * Requires authentication. The ``invited_by`` field is set from the
 * authenticated person's ID.
 *
 * @endpoint POST /invitations/
 */
async ({ invitationCreate }: { invitationCreate?: InvitationCreate } = {}): Promise<InvitationResponse> => {
        const response = await __transport.axios.post('/invitations/', invitationCreate)
        return ApiResponse.create(response.data, response)},
  get: /**
 * Get Invitations
 *
 * List invitations.
 * 
 * Tenant scoping applied via:
 * - ``tenant_id`` query param (explicit, takes priority)
 * - X-Tenant-ID header → ORM session filter (fallback)
 *
 * @endpoint GET /invitations/
 */
async ({ status, _tenantId }: { status?: InvitationStatus, _tenantId?: string } = {}): Promise<ApiResponse<InvitationResponse[]>> => {
        const response = await __transport.axios.get('/invitations/', { params: {status, 'tenant_id': _tenantId} })
        return ApiResponse.create(response.data, response)},
  byToken: /**
 * Get Invitation By Token
 *
 * Get invitation details by token (public — no auth required).
 * 
 * Used by the frontend to display the invitation page before the user
 * signs in or registers.
 *
 * @endpoint GET /invitations/by-token/{token}
 */
async ({ token }: { token?: string } = {}): Promise<InvitationResponse> => {
        const response = await __transport.axios.get(`/invitations/by-token/${token}`)
        return ApiResponse.create(response.data, response)},
  accept: /**
 * Accept Invitation
 *
 * Accept an invitation (authenticated).
 * 
 * The authenticated user's email **must** match the invitation's email.
 * Returns the accepted invitation, or the already-accepted invitation
 * if this is a repeat call (idempotent).
 *
 * @endpoint POST /invitations/{token}/accept
 */
async ({ token }: { token?: string } = {}): Promise<InvitationResponse> => {
        const response = await __transport.axios.post(`/invitations/${token}/accept`, {})
        return ApiResponse.create(response.data, response)},
  resend: /**
 * Resend Invitation
 *
 * Resend invitation email.
 *
 * @endpoint POST /invitations/{invitation_id}/resend
 */
async ({ invitationId }: { invitationId?: string } = {}): Promise<InvitationResponse> => {
        const response = await __transport.axios.post(`/invitations/${invitationId}/resend`, {})
        return ApiResponse.create(response.data, response)},
  repair: /**
 * Repair Invitation
 *
 * Replay idempotent provisioning for an accepted invitation.
 *
 * @endpoint POST /invitations/{invitation_id}/repair
 */
async ({ invitationId }: { invitationId?: string } = {}): Promise<InvitationResponse> => {
        const response = await __transport.axios.post(`/invitations/${invitationId}/repair`, {})
        return ApiResponse.create(response.data, response)}
};

/**
 * API functions for blox endpoints
 */
const blox = {
  templates: /**
 * List Templates Endpoint
 *
 * @endpoint GET /blox/templates
 */
async (): Promise<ApiResponse<BloxTemplateRead[]>> => {
        const response = await __transport.axios.get('/blox/templates')
        return ApiResponse.create(response.data, response)},
  blueprints: {
    plan: /**
 * Plan Blueprint Endpoint
 *
 * Dry-run a site blueprint (what would be created vs reused, plus issues).
 *
 * @endpoint POST /blox/blueprints/plan
 */
async ({ siteBlueprint }: { siteBlueprint?: SiteBlueprint } = {}): Promise<BlueprintPlan> => {
        const response = await __transport.axios.post('/blox/blueprints/plan', siteBlueprint)
        return ApiResponse.create(response.data, response)},
    apply: /**
 * Apply Blueprint Endpoint
 *
 * Create a website with its collections, global blocks and pages.
 *
 * @endpoint POST /blox/blueprints/apply
 */
async ({ siteBlueprint }: { siteBlueprint?: SiteBlueprint } = {}): Promise<BlueprintApplyResult> => {
        const response = await __transport.axios.post('/blox/blueprints/apply', siteBlueprint)
        return ApiResponse.create(response.data, response)}
  },
  websites: {
    getByWebsiteId: /**
 * Get Website Endpoint
 *
 * @endpoint GET /blox/websites/{website_id}
 */
async ({ websiteId, locale }: { websiteId?: string, locale?: string } = {}): Promise<WebsiteRead> => {
        const response = await __transport.axios.get(`/blox/websites/${websiteId}`, { params: {locale} })
        return ApiResponse.create(response.data, response)},
    put: /**
 * Update Website Endpoint
 *
 * @endpoint PUT /blox/websites/{website_id}
 */
async ({ websiteId, websiteUpdate }: { websiteId?: string, websiteUpdate?: WebsiteUpdate } = {}): Promise<WebsiteRead> => {
        const response = await __transport.axios.put(`/blox/websites/${websiteId}`, websiteUpdate)
        return ApiResponse.create(response.data, response)},
    delete: /**
 * Delete Website Endpoint
 *
 * @endpoint DELETE /blox/websites/{website_id}
 */
async ({ websiteId }: { websiteId?: string } = {}) => {
        const response = await __transport.axios.delete(`/blox/websites/${websiteId}`)
        return ApiResponse.create(response.data, response)},
    get: /**
 * List Websites Endpoint
 *
 * @endpoint GET /blox/websites
 */
async (): Promise<ApiResponse<WebsiteRead[]>> => {
        const response = await __transport.axios.get('/blox/websites')
        return ApiResponse.create(response.data, response)},
    post: /**
 * Create Website Endpoint
 *
 * @endpoint POST /blox/websites
 */
async ({ websiteCreate }: { websiteCreate?: WebsiteCreate } = {}): Promise<WebsiteRead> => {
        const response = await __transport.axios.post('/blox/websites', websiteCreate)
        return ApiResponse.create(response.data, response)},
    deployment: {
      post: /**
 * Provision Deployment Endpoint
 *
 * @endpoint POST /blox/websites/{website_id}/deployment
 */
async ({ websiteId, deploymentProvisionRequest }: { websiteId?: string, deploymentProvisionRequest?: DeploymentProvisionRequest } = {}): Promise<ProvisionResult> => {
        const response = await __transport.axios.post(`/blox/websites/${websiteId}/deployment`, deploymentProvisionRequest)
        return ApiResponse.create(response.data, response)},
      get: /**
 * Deployment Status Endpoint
 *
 * @endpoint GET /blox/websites/{website_id}/deployment
 */
async ({ websiteId }: { websiteId?: string } = {}): Promise<DeploymentRead> => {
        const response = await __transport.axios.get(`/blox/websites/${websiteId}/deployment`)
        return ApiResponse.create(response.data, response)},
      delete: /**
 * Disconnect Deployment Endpoint
 *
 * @endpoint DELETE /blox/websites/{website_id}/deployment
 */
async ({ websiteId }: { websiteId?: string } = {}): Promise<DeploymentRead> => {
        const response = await __transport.axios.delete(`/blox/websites/${websiteId}/deployment`)
        return ApiResponse.create(response.data, response)},
      retry: /**
 * Retry Deployment Endpoint
 *
 * @endpoint POST /blox/websites/{website_id}/deployment/retry
 */
async ({ websiteId }: { websiteId?: string } = {}): Promise<ProvisionResult> => {
        const response = await __transport.axios.post(`/blox/websites/${websiteId}/deployment/retry`, {})
        return ApiResponse.create(response.data, response)},
      redeploy: /**
 * Redeploy Endpoint
 *
 * @endpoint POST /blox/websites/{website_id}/deployment/redeploy
 */
async ({ websiteId }: { websiteId?: string } = {}): Promise<DeploymentRead> => {
        const response = await __transport.axios.post(`/blox/websites/${websiteId}/deployment/redeploy`, {})
        return ApiResponse.create(response.data, response)}
    },
    manifest: {
      put: /**
 * Set Manifest Endpoint
 *
 * Sync the block manifest from the deployed frontend template.
 * 
 * Called at build/deploy time (or on GitHub import) with the contents of the
 * site's generated `.blox-manifest.json`. Powers the agent's `describe_blocks`
 * tool and server-side page validation.
 *
 * @endpoint PUT /blox/websites/{website_id}/manifest
 */
async ({ websiteId, blockManifest }: { websiteId?: string, blockManifest?: BlockManifest } = {}): Promise<WebsiteRead> => {
        const response = await __transport.axios.put(`/blox/websites/${websiteId}/manifest`, blockManifest)
        return ApiResponse.create(response.data, response)},
      get: /**
 * Get Manifest Endpoint
 *
 * @endpoint GET /blox/websites/{website_id}/manifest
 */
async ({ websiteId }: { websiteId?: string } = {}): Promise<BlockManifest> => {
        const response = await __transport.axios.get(`/blox/websites/${websiteId}/manifest`)
        return ApiResponse.create(response.data, response)}
    },
    pages: {
      getByPageId: /**
 * Get Page Endpoint
 *
 * @endpoint GET /blox/websites/{website_id}/pages/{page_id}
 */
async ({ websiteId, pageId, locale }: { websiteId?: string, pageId?: string, locale?: string } = {}): Promise<PageRead> => {
        const response = await __transport.axios.get(`/blox/websites/${websiteId}/pages/${pageId}`, { params: {locale} })
        return ApiResponse.create(response.data, response)},
      put: /**
 * Update Page Endpoint
 *
 * @endpoint PUT /blox/websites/{website_id}/pages/{page_id}
 */
async ({ websiteId, pageId, locale, pageUpdate }: { websiteId?: string, pageId?: string, locale?: string, pageUpdate?: PageUpdate } = {}): Promise<PageRead> => {
        const response = await __transport.axios.put(`/blox/websites/${websiteId}/pages/${pageId}`, pageUpdate, { params: {locale} })
        return ApiResponse.create(response.data, response)},
      delete: /**
 * Delete Page Endpoint
 *
 * @endpoint DELETE /blox/websites/{website_id}/pages/{page_id}
 */
async ({ websiteId, pageId }: { websiteId?: string, pageId?: string } = {}) => {
        const response = await __transport.axios.delete(`/blox/websites/${websiteId}/pages/${pageId}`)
        return ApiResponse.create(response.data, response)},
      post: /**
 * Create Page Endpoint
 *
 * @endpoint POST /blox/websites/{website_id}/pages
 */
async ({ websiteId, pageCreate }: { websiteId?: string, pageCreate?: PageCreate } = {}): Promise<PageRead> => {
        const response = await __transport.axios.post(`/blox/websites/${websiteId}/pages`, pageCreate)
        return ApiResponse.create(response.data, response)},
      get: /**
 * List Pages Endpoint
 *
 * @endpoint GET /blox/websites/{website_id}/pages
 */
async ({ websiteId, locale }: { websiteId?: string, locale?: string } = {}): Promise<ApiResponse<PageRead[]>> => {
        const response = await __transport.axios.get(`/blox/websites/${websiteId}/pages`, { params: {locale} })
        return ApiResponse.create(response.data, response)},
      versions: {
        get: /**
 * List Versions Endpoint
 *
 * @endpoint GET /blox/websites/{website_id}/pages/{page_id}/versions
 */
async ({ websiteId, pageId }: { websiteId?: string, pageId?: string } = {}): Promise<ApiResponse<PageVersionRead[]>> => {
        const response = await __transport.axios.get(`/blox/websites/${websiteId}/pages/${pageId}/versions`)
        return ApiResponse.create(response.data, response)},
        restore: /**
 * Restore Version Endpoint
 *
 * @endpoint POST /blox/websites/{website_id}/pages/{page_id}/versions/{version_id}/restore
 */
async ({ websiteId, pageId, versionId }: { websiteId?: string, pageId?: string, versionId?: string } = {}): Promise<PageRead> => {
        const response = await __transport.axios.post(`/blox/websites/${websiteId}/pages/${pageId}/versions/${versionId}/restore`, {})
        return ApiResponse.create(response.data, response)}
      },
      publish: /**
 * Publish Page Endpoint
 *
 * Publish a single page (promote its draft), then fire publish hooks.
 *
 * @endpoint POST /blox/websites/{website_id}/pages/{page_id}/publish
 */
async ({ websiteId, pageId }: { websiteId?: string, pageId?: string } = {}): Promise<PublishResult> => {
        const response = await __transport.axios.post(`/blox/websites/${websiteId}/pages/${pageId}/publish`, {})
        return ApiResponse.create(response.data, response)},
      schema: /**
 * Page Schema Endpoint
 *
 * @endpoint GET /blox/websites/{website_id}/pages/{page_id}/schema
 */
async ({ websiteId, pageId }: { websiteId?: string, pageId?: string } = {}): Promise<PageSchemaRead> => {
        const response = await __transport.axios.get(`/blox/websites/${websiteId}/pages/${pageId}/schema`)
        return ApiResponse.create(response.data, response)},
      resolve: /**
 * Page Resolve Endpoint
 *
 * @endpoint GET /blox/websites/{website_id}/pages/{page_id}/resolve
 */
async ({ websiteId, pageId, locale }: { websiteId?: string, pageId?: string, locale?: string } = {}): Promise<PageResolveRead> => {
        const response = await __transport.axios.get(`/blox/websites/${websiteId}/pages/${pageId}/resolve`, { params: {locale} })
        return ApiResponse.create(response.data, response)}
    },
    publish: /**
 * Publish Website Endpoint
 *
 * @endpoint POST /blox/websites/{website_id}/publish
 */
async ({ websiteId }: { websiteId?: string } = {}): Promise<PublishResult> => {
        const response = await __transport.axios.post(`/blox/websites/${websiteId}/publish`, {})
        return ApiResponse.create(response.data, response)},
    globalBlocks: {
      put: /**
 * Upsert Global Block Endpoint
 *
 * Upsert one global block's props. `?locale=xx` stores a translation (§D.5).
 *
 * @endpoint PUT /blox/websites/{website_id}/global-blocks/{block_type}
 */
async ({ websiteId, blockType, locale, globalBlockUpsert }: { websiteId?: string, blockType?: string, locale?: string, globalBlockUpsert?: GlobalBlockUpsert } = {}): Promise<GlobalBlockRead> => {
        const response = await __transport.axios.put(`/blox/websites/${websiteId}/global-blocks/${blockType}`, globalBlockUpsert, { params: {locale} })
        return ApiResponse.create(response.data, response)},
      delete: /**
 * Delete Global Block Endpoint
 *
 * @endpoint DELETE /blox/websites/{website_id}/global-blocks/{block_type}
 */
async ({ websiteId, blockType }: { websiteId?: string, blockType?: string } = {}) => {
        const response = await __transport.axios.delete(`/blox/websites/${websiteId}/global-blocks/${blockType}`)
        return ApiResponse.create(response.data, response)},
      get: /**
 * List Global Blocks Endpoint
 *
 * @endpoint GET /blox/websites/{website_id}/global-blocks
 */
async ({ websiteId, locale }: { websiteId?: string, locale?: string } = {}): Promise<ApiResponse<GlobalBlockRead[]>> => {
        const response = await __transport.axios.get(`/blox/websites/${websiteId}/global-blocks`, { params: {locale} })
        return ApiResponse.create(response.data, response)}
    },
    resolvePath: /**
 * Resolve Path Endpoint
 *
 * @endpoint GET /blox/websites/{website_id}/resolve-path
 */
async ({ websiteId, path, locale }: { websiteId?: string, path?: string, locale?: string } = {}): Promise<PageResolveRead> => {
        const response = await __transport.axios.get(`/blox/websites/${websiteId}/resolve-path`, { params: {path, locale} })
        return ApiResponse.create(response.data, response)},
    redirects: {
      put: /**
 * Update Redirect Endpoint
 *
 * @endpoint PUT /blox/websites/{website_id}/redirects/{redirect_id}
 */
async ({ websiteId, redirectId, redirectUpdate }: { websiteId?: string, redirectId?: string, redirectUpdate?: RedirectUpdate } = {}): Promise<RedirectRead> => {
        const response = await __transport.axios.put(`/blox/websites/${websiteId}/redirects/${redirectId}`, redirectUpdate)
        return ApiResponse.create(response.data, response)},
      delete: /**
 * Delete Redirect Endpoint
 *
 * @endpoint DELETE /blox/websites/{website_id}/redirects/{redirect_id}
 */
async ({ websiteId, redirectId }: { websiteId?: string, redirectId?: string } = {}) => {
        const response = await __transport.axios.delete(`/blox/websites/${websiteId}/redirects/${redirectId}`)
        return ApiResponse.create(response.data, response)},
      post: /**
 * Create Redirect Endpoint
 *
 * @endpoint POST /blox/websites/{website_id}/redirects
 */
async ({ websiteId, redirectCreate }: { websiteId?: string, redirectCreate?: RedirectCreate } = {}): Promise<RedirectRead> => {
        const response = await __transport.axios.post(`/blox/websites/${websiteId}/redirects`, redirectCreate)
        return ApiResponse.create(response.data, response)},
      get: /**
 * List Redirects Endpoint
 *
 * @endpoint GET /blox/websites/{website_id}/redirects
 */
async ({ websiteId, offset, limit = 100 }: { websiteId?: string, offset?: number, limit?: number } = {}): Promise<ApiResponse<RedirectRead[]>> => {
        const response = await __transport.axios.get(`/blox/websites/${websiteId}/redirects`, { params: {offset, limit} })
        return ApiResponse.create(response.data, response)}
    },
    analytics: {
      stats: {
        realtime: /**
 * Analytics Realtime Endpoint
 *
 * @endpoint GET /blox/websites/{website_id}/analytics/stats/realtime
 */
async ({ websiteId }: { websiteId?: string } = {}): Promise<PlausibleRealtimeVisitors> => {
        const response = await __transport.axios.get(`/blox/websites/${websiteId}/analytics/stats/realtime`)
        return ApiResponse.create(response.data, response)},
        aggregate: /**
 * Analytics Aggregate Endpoint
 *
 * @endpoint GET /blox/websites/{website_id}/analytics/stats/aggregate
 */
async ({ websiteId, period = '30d', date, metrics, filters }: { websiteId?: string, period?: string, date?: string, metrics?: string, filters?: string } = {}): Promise<PlausibleAggregateMetrics> => {
        const response = await __transport.axios.get(`/blox/websites/${websiteId}/analytics/stats/aggregate`, { params: {period, date, metrics, filters} })
        return ApiResponse.create(response.data, response)},
        timeseries: /**
 * Analytics Timeseries Endpoint
 *
 * @endpoint GET /blox/websites/{website_id}/analytics/stats/timeseries
 */
async ({ websiteId, period = '30d', date, metrics, filters }: { websiteId?: string, period?: string, date?: string, metrics?: string, filters?: string } = {}): Promise<PlausibleTimeseriesRead> => {
        const response = await __transport.axios.get(`/blox/websites/${websiteId}/analytics/stats/timeseries`, { params: {period, date, metrics, filters} })
        return ApiResponse.create(response.data, response)},
        breakdown: /**
 * Analytics Breakdown Endpoint
 *
 * @endpoint GET /blox/websites/{website_id}/analytics/stats/breakdown
 */
async ({ websiteId, property, period = '30d', date, metrics, filters, limit = 100, page = 1 }: { websiteId?: string, property?: string, period?: string, date?: string, metrics?: string, filters?: string, limit?: number, page?: number } = {}): Promise<PlausibleBreakdownRead> => {
        const response = await __transport.axios.get(`/blox/websites/${websiteId}/analytics/stats/breakdown`, { params: {property, period, date, metrics, filters, limit, page} })
        return ApiResponse.create(response.data, response)}
      }
    }
  },
  sites: /**
 * Public Website Ref Endpoint
 *
 * Public: resolve a site name to its id for the anonymous renderer.
 * 
 * Returns only ``{id, name}`` — never settings, hooks, integrations or
 * manifests (those stay behind ``blox:read``).
 *
 * @endpoint GET /blox/sites/{website_name}
 */
async ({ websiteName }: { websiteName?: string } = {}): Promise<PublicWebsiteRef> => {
        const response = await __transport.axios.get(`/blox/sites/${websiteName}`)
        return ApiResponse.create(response.data, response)},
  deployment: {
    validateRepository: /**
 * Validate Repository Endpoint
 *
 * @endpoint POST /blox/deployment/validate-repository
 */
async ({ repositoryValidationRequest }: { repositoryValidationRequest?: RepositoryValidationRequest } = {}): Promise<RepositoryValidationRead> => {
        const response = await __transport.axios.post('/blox/deployment/validate-repository', repositoryValidationRequest)
        return ApiResponse.create(response.data, response)}
  }
};

/**
 * API functions for bloxBuild endpoints
 */
const bloxBuild = {
  websites: {
    published: /**
 * Published Bundle
 *
 * @endpoint GET /blox-build/websites/{website_id}/published
 */
async ({ websiteId, locale, authorization }: { websiteId?: string, locale?: string, authorization?: string } = {}): Promise<BuildBundleRead> => {
        const response = await __transport.axios.get(`/blox-build/websites/${websiteId}/published`, { params: {locale, authorization} })
        return ApiResponse.create(response.data, response)},
    collections: /**
 * Published Collection
 *
 * Return only collections referenced by this website's published pages.
 *
 * @endpoint GET /blox-build/websites/{website_id}/collections/{store}/{collection}
 */
async ({ websiteId, store, collection, locale, q, offset, limit = 200, authorization }: { websiteId?: string, store?: string, collection?: string, locale?: string, q?: string, offset?: number, limit?: number, authorization?: string } = {}): Promise<BuildCollectionRead> => {
        const response = await __transport.axios.get(`/blox-build/websites/${websiteId}/collections/${store}/${collection}`, { params: {locale, q, offset, limit, authorization} })
        return ApiResponse.create(response.data, response)},
    manifest: /**
 * Upload Manifest
 *
 * @endpoint PUT /blox-build/websites/{website_id}/manifest
 */
async ({ websiteId, authorization, manifestUpload }: { websiteId?: string, authorization?: string, manifestUpload?: ManifestUpload } = {}) => {
        const response = await __transport.axios.put(`/blox-build/websites/${websiteId}/manifest`, manifestUpload, { params: {authorization} })
        return ApiResponse.create(response.data, response)}
  }
};

/**
 * API functions for datastore endpoints
 */
const datastore = {
  stores: /**
 * List Stores Endpoint
 *
 * @endpoint GET /datastore/stores
 */
async (): Promise<ApiResponse<StoreCatalogRead[]>> => {
        const response = await __transport.axios.get('/datastore/stores')
        return ApiResponse.create(response.data, response)},
  schemas: {
    getByCollectionName: /**
 * Get Schema Endpoint
 *
 * @endpoint GET /datastore/{store}/schemas/{collection_name}
 */
async ({ store, collectionName }: { store?: string, collectionName?: string } = {}): Promise<CollectionSchemaRead> => {
        const response = await __transport.axios.get(`/datastore/${store}/schemas/${collectionName}`)
        return ApiResponse.create(response.data, response)},
    put: /**
 * Update Schema Endpoint
 *
 * @endpoint PUT /datastore/{store}/schemas/{collection_name}
 */
async ({ store, collectionName, collectionSchemaUpdate }: { store?: string, collectionName?: string, collectionSchemaUpdate?: CollectionSchemaUpdate } = {}): Promise<CollectionSchemaRead> => {
        const response = await __transport.axios.put(`/datastore/${store}/schemas/${collectionName}`, collectionSchemaUpdate)
        return ApiResponse.create(response.data, response)},
    delete: /**
 * Delete Schema Endpoint
 *
 * @endpoint DELETE /datastore/{store}/schemas/{collection_name}
 */
async ({ store, collectionName }: { store?: string, collectionName?: string } = {}) => {
        const response = await __transport.axios.delete(`/datastore/${store}/schemas/${collectionName}`)
        return ApiResponse.create(response.data, response)},
    post: /**
 * Create Schema Endpoint
 *
 * @endpoint POST /datastore/{store}/schemas
 */
async ({ store, collectionSchemaCreate }: { store?: string, collectionSchemaCreate?: CollectionSchemaCreate } = {}): Promise<CollectionSchemaRead> => {
        const response = await __transport.axios.post(`/datastore/${store}/schemas`, collectionSchemaCreate)
        return ApiResponse.create(response.data, response)},
    get: /**
 * List Schemas Endpoint
 *
 * @endpoint GET /datastore/{store}/schemas
 */
async ({ store }: { store?: string } = {}): Promise<ApiResponse<CollectionSchemaRead[]>> => {
        const response = await __transport.axios.get(`/datastore/${store}/schemas`)
        return ApiResponse.create(response.data, response)},
    renameField: /**
 * Rename Field Endpoint
 *
 * @endpoint POST /datastore/{store}/schemas/{collection_name}/rename-field
 */
async ({ store, collectionName, renameFieldRequest }: { store?: string, collectionName?: string, renameFieldRequest?: RenameFieldRequest } = {}): Promise<RenameFieldResult> => {
        const response = await __transport.axios.post(`/datastore/${store}/schemas/${collectionName}/rename-field`, renameFieldRequest)
        return ApiResponse.create(response.data, response)}
  },
  collections: {
    post: /**
 * Create Collection Item Endpoint
 *
 * @endpoint POST /datastore/{store}/collections/{collection_name}
 */
async ({ store, collectionName, locale = 'en', _includeUnlocalized, collectionItemCreate }: { store?: string, collectionName?: string, locale?: string, _includeUnlocalized?: boolean, collectionItemCreate?: CollectionItemCreate } = {}): Promise<CollectionItemRead> => {
        const response = await __transport.axios.post(`/datastore/${store}/collections/${collectionName}`, collectionItemCreate, { params: {locale, 'include_unlocalized': _includeUnlocalized} })
        return ApiResponse.create(response.data, response)},
    get: /**
 * List Collection Items Endpoint
 *
 * @endpoint GET /datastore/{store}/collections/{collection_name}
 */
async ({ store, collectionName, search, q, include = [], select = [], _sortBy, _sortDirection = 'asc', offset, limit = 100, locale = 'en', _includeUnlocalized, _includeDeleted }: { store?: string, collectionName?: string, search?: string, q?: string, include?: string[], select?: string[], _sortBy?: string, _sortDirection?: string, offset?: number, limit?: number, locale?: string, _includeUnlocalized?: boolean, _includeDeleted?: boolean } = {}): Promise<ApiResponse<CollectionItemRead[]>> => {
        const response = await __transport.axios.get(`/datastore/${store}/collections/${collectionName}`, { params: {search, q, include, select, 'sort_by': _sortBy, 'sort_direction': _sortDirection, offset, limit, locale, 'include_unlocalized': _includeUnlocalized, 'include_deleted': _includeDeleted} })
        return ApiResponse.create(response.data, response)},
    item: {
      get: /**
 * Get Collection Item Endpoint
 *
 * @endpoint GET /datastore/{store}/collections/{collection_name}/item/{item_id}
 */
async ({ store, collectionName, itemId, include = [], select = [], locale = 'en', _includeUnlocalized }: { store?: string, collectionName?: string, itemId?: string, include?: string[], select?: string[], locale?: string, _includeUnlocalized?: boolean } = {}): Promise<CollectionItemRead> => {
        const response = await __transport.axios.get(`/datastore/${store}/collections/${collectionName}/item/${itemId}`, { params: {include, select, locale, 'include_unlocalized': _includeUnlocalized} })
        return ApiResponse.create(response.data, response)},
      put: /**
 * Update Collection Item Endpoint
 *
 * @endpoint PUT /datastore/{store}/collections/{collection_name}/item/{item_id}
 */
async ({ store, collectionName, itemId, locale = 'en', _includeUnlocalized, collectionItemUpdate }: { store?: string, collectionName?: string, itemId?: string, locale?: string, _includeUnlocalized?: boolean, collectionItemUpdate?: CollectionItemUpdate } = {}): Promise<CollectionItemRead> => {
        const response = await __transport.axios.put(`/datastore/${store}/collections/${collectionName}/item/${itemId}`, collectionItemUpdate, { params: {locale, 'include_unlocalized': _includeUnlocalized} })
        return ApiResponse.create(response.data, response)},
      delete: /**
 * Delete Collection Item Endpoint
 *
 * @endpoint DELETE /datastore/{store}/collections/{collection_name}/item/{item_id}
 */
async ({ store, collectionName, itemId, force }: { store?: string, collectionName?: string, itemId?: string, force?: boolean } = {}) => {
        const response = await __transport.axios.delete(`/datastore/${store}/collections/${collectionName}/item/${itemId}`, { params: {force} })
        return ApiResponse.create(response.data, response)},
      locales: /**
 * Delete Item Locale Endpoint
 *
 * Remove one language from an item (its translations + availability).
 * 
 * Refuses to remove the last remaining locale (409) — delete the item instead.
 *
 * @endpoint DELETE /datastore/{store}/collections/{collection_name}/item/{item_id}/locales/{locale_code}
 */
async ({ store, collectionName, itemId, localeCode, locale = 'en', _includeUnlocalized }: { store?: string, collectionName?: string, itemId?: string, localeCode?: string, locale?: string, _includeUnlocalized?: boolean } = {}): Promise<CollectionItemRead> => {
        const response = await __transport.axios.delete(`/datastore/${store}/collections/${collectionName}/item/${itemId}/locales/${localeCode}`, { params: {locale, 'include_unlocalized': _includeUnlocalized} })
        return ApiResponse.create(response.data, response)},
      restore: /**
 * Restore Collection Item Endpoint
 *
 * @endpoint POST /datastore/{store}/collections/{collection_name}/item/{item_id}/restore
 */
async ({ store, collectionName, itemId, locale = 'en', _includeUnlocalized }: { store?: string, collectionName?: string, itemId?: string, locale?: string, _includeUnlocalized?: boolean } = {}): Promise<CollectionItemRead> => {
        const response = await __transport.axios.post(`/datastore/${store}/collections/${collectionName}/item/${itemId}/restore`, {}, { params: {locale, 'include_unlocalized': _includeUnlocalized} })
        return ApiResponse.create(response.data, response)},
      move: /**
 * Move Collection Item Endpoint
 *
 * @endpoint PATCH /datastore/{store}/collections/{collection_name}/item/{item_id}/move
 */
async ({ store, collectionName, itemId, moveRequest }: { store?: string, collectionName?: string, itemId?: string, moveRequest?: MoveRequest } = {}): Promise<CollectionItemRead> => {
        const response = await __transport.axios.patch(`/datastore/${store}/collections/${collectionName}/item/${itemId}/move`, moveRequest)
        return ApiResponse.create(response.data, response)}
    }
  }
};

/**
 * API functions for form endpoints
 */
const form = {
  get: /**
 * List Form Definitions
 *
 * @endpoint GET /form/
 */
async ({ _activeOnly = true, offset, limit = 100 }: { _activeOnly?: boolean, offset?: number, limit?: number } = {}): Promise<ApiResponse<FormDefinitionRead[]>> => {
        const response = await __transport.axios.get('/form/', { params: {'active_only': _activeOnly, offset, limit} })
        return ApiResponse.create(response.data, response)},
  submit: {
    postByFormName: /**
 * Submit Form By Name
 *
 * Submit a form with a specific form name (from path parameter).
 * 
 * Anonymous callers may only submit to public forms; see ``_submission_target``.
 * Body is flat business data; control fields live under ``_meta``.
 * The path ``form_name`` wins over ``_meta.form_name``.
 *
 * @endpoint POST /form/submit/{form_name}
 */
async ({ formName, formSubmissionCreate }: { formName?: string, formSubmissionCreate?: FormSubmissionCreate } = {}): Promise<FormSubmissionRead> => {
        const response = await __transport.axios.post(`/form/submit/${formName}`, formSubmissionCreate)
        return ApiResponse.create(response.data, response)},
    post: /**
 * Submit Form
 *
 * Submit a form (flexible endpoint with optional form_name in body).
 * 
 * Anonymous callers may only submit to public forms; see ``_submission_target``.
 * 
 * Body is flat business data; control fields live under ``_meta``:
 * - Single-step: omit ``_meta.form_complete`` (defaults to True)
 * - Multi-step Step 1: set ``_meta.form_complete`` = false
 * - Multi-step Step 2+: use PUT /submissions/{id} endpoint
 *
 * @endpoint POST /form/submit
 */
async ({ formSubmissionCreate }: { formSubmissionCreate?: FormSubmissionCreate } = {}): Promise<FormSubmissionRead> => {
        const response = await __transport.axios.post('/form/submit', formSubmissionCreate)
        return ApiResponse.create(response.data, response)}
  },
  submissions: {
    getBySubmissionId: /**
 * Get Submission
 *
 * Get a form submission by ID.
 *
 * @endpoint GET /form/submissions/{submission_id}
 */
async ({ submissionId }: { submissionId?: string } = {}): Promise<FormSubmissionRead> => {
        const response = await __transport.axios.get(`/form/submissions/${submissionId}`)
        return ApiResponse.create(response.data, response)},
    put: /**
 * Update Submission
 *
 * Update a form submission.
 * 
 * Body is flat business data; control fields live under ``_meta``.
 *
 * @endpoint PUT /form/submissions/{submission_id}
 */
async ({ submissionId, formSubmissionUpdate }: { submissionId?: string, formSubmissionUpdate?: FormSubmissionUpdate } = {}): Promise<FormSubmissionRead> => {
        const response = await __transport.axios.put(`/form/submissions/${submissionId}`, formSubmissionUpdate)
        return ApiResponse.create(response.data, response)},
    delete: /**
 * Delete Submission
 *
 * Delete a form submission by ID.
 *
 * @endpoint DELETE /form/submissions/{submission_id}
 */
async ({ submissionId }: { submissionId?: string } = {}) => {
        const response = await __transport.axios.delete(`/form/submissions/${submissionId}`)
        return ApiResponse.create(response.data, response)},
    get: /**
 * List Submissions
 *
 * List form submissions with pagination, SCIM filter, sort, and search.
 * 
 * - Pagination: ``offset`` / ``limit`` (headers carry total).
 * - Filter (``?q=``): SCIM 2.0. ``utm_source`` etc. are real columns; any other
 *   bare field (e.g. ``email``) is matched against the ``form_data`` JSONB body.
 * - Sort (``?sort=``): ``field:dir`` — form_data keys sortable too.
 * - Search (``?search=``): free text over form name + form_data body.
 *
 * @endpoint GET /form/submissions
 */
async ({ _formName, status, _personId, _fieldKey, _fieldValue, offset, limit = 100, q, sort, search }: { _formName?: string, status?: FormSubStatus, _personId?: string, _fieldKey?: string, _fieldValue?: string, offset?: number, limit?: number, q?: string, sort?: string, search?: string } = {}): Promise<ApiResponse<FormSubmissionRead[]>> => {
        const response = await __transport.axios.get('/form/submissions', { params: {'form_name': _formName, status, 'person_id': _personId, 'field_key': _fieldKey, 'field_value': _fieldValue, offset, limit, q, sort, search} })
        return ApiResponse.create(response.data, response)},
    step: /**
 * Submit Form Step
 *
 * Submit a step in a multi-step form.
 * 
 * Body is flat business data; control fields live under ``_meta``.
 *
 * @endpoint POST /form/submissions/{submission_id}/step
 */
async ({ submissionId, formStepUpdate }: { submissionId?: string, formStepUpdate?: FormStepUpdate } = {}): Promise<FormSubmissionRead> => {
        const response = await __transport.axios.post(`/form/submissions/${submissionId}/step`, formStepUpdate)
        return ApiResponse.create(response.data, response)},
    processAbandoned: /**
 * Trigger Abandoned Check
 *
 * Manually trigger the abandoned form checker.
 *
 * @endpoint POST /form/submissions/process-abandoned
 */
async ({ _minutesInactive }: { _minutesInactive?: number } = {}): Promise<{ [key: string]: any }> => {
        const response = await __transport.axios.post('/form/submissions/process-abandoned', {}, { params: {'minutes_inactive': _minutesInactive} })
        return response.data},
    processStuck: /**
 * Trigger Stuck Check
 *
 * Re-emit completed for submitted submissions that were never processed.
 *
 * @endpoint POST /form/submissions/process-stuck
 */
async (): Promise<{ [key: string]: any }> => {
        const response = await __transport.axios.post('/form/submissions/process-stuck', {})
        return response.data},
    recoverStuck: /**
 * Recover Stuck
 *
 * One-shot recovery: re-emit ``completed`` for submitted rows missing
 * ``processed_at``, then stamp ``processed_at`` so the scheduler cannot loop.
 *
 * @endpoint POST /form/submissions/recover-stuck
 */
async ({ _dryRun }: { _dryRun?: boolean } = {}): Promise<{ [key: string]: any }> => {
        const response = await __transport.axios.post('/form/submissions/recover-stuck', {}, { params: {'dry_run': _dryRun} })
        return response.data}
  },
  names: /**
 * Get Form Names
 *
 * Get list of unique form names.
 *
 * @endpoint GET /form/names
 */
async (): Promise<ApiResponse<string[]>> => {
        const response = await __transport.axios.get('/form/names')
        return ApiResponse.create(response.data, response)},
  definitions: {
    get: /**
 * Get Form Definition
 *
 * Get a form definition by ID.
 *
 * @endpoint GET /form/definitions/{definition_id}
 */
async ({ definitionId, locale }: { definitionId?: string, locale?: string } = {}): Promise<FormDefinitionRead> => {
        const response = await __transport.axios.get(`/form/definitions/${definitionId}`, { params: {locale} })
        return ApiResponse.create(response.data, response)},
    put: /**
 * Update Form Definition
 *
 * Update a form definition.
 *
 * @endpoint PUT /form/definitions/{definition_id}
 */
async ({ definitionId, formDefinitionUpdate }: { definitionId?: string, formDefinitionUpdate?: FormDefinitionUpdate } = {}): Promise<FormDefinitionRead> => {
        const response = await __transport.axios.put(`/form/definitions/${definitionId}`, formDefinitionUpdate)
        return ApiResponse.create(response.data, response)},
    post: /**
 * Create Form Definition
 *
 * Create a new form definition with JSON Schema validation.
 *
 * @endpoint POST /form/definitions
 */
async ({ formDefinitionCreate }: { formDefinitionCreate?: FormDefinitionCreate } = {}): Promise<FormDefinitionRead> => {
        const response = await __transport.axios.post('/form/definitions', formDefinitionCreate)
        return ApiResponse.create(response.data, response)},
    revisions: {
      getByRevisionId: /**
 * Get Form Definition Revision
 *
 * @endpoint GET /form/definitions/{definition_id}/revisions/{revision_id}
 */
async ({ definitionId, revisionId }: { definitionId?: string, revisionId?: string } = {}): Promise<FormDefinitionRevisionRead> => {
        const response = await __transport.axios.get(`/form/definitions/${definitionId}/revisions/${revisionId}`)
        return ApiResponse.create(response.data, response)},
      get: /**
 * List Form Definition Revisions
 *
 * @endpoint GET /form/definitions/{definition_id}/revisions
 */
async ({ definitionId, offset, limit = 100 }: { definitionId?: string, offset?: number, limit?: number } = {}): Promise<ApiResponse<FormDefinitionRevisionRead[]>> => {
        const response = await __transport.axios.get(`/form/definitions/${definitionId}/revisions`, { params: {offset, limit} })
        return ApiResponse.create(response.data, response)}
    },
    byName: /**
 * Get Form Definition By Name
 *
 * Get a form definition by name.
 *
 * @endpoint GET /form/definitions/by-name/{name}
 */
async ({ name, locale }: { name?: string, locale?: string } = {}): Promise<FormDefinitionRead> => {
        const response = await __transport.axios.get(`/form/definitions/by-name/${name}`, { params: {locale} })
        return ApiResponse.create(response.data, response)},
    locales: {
      get: /**
 * Get Form Definition Locale
 *
 * Read a locale overlay, including unpublished admin drafts.
 *
 * @endpoint GET /form/definitions/{definition_id}/locales/{locale}
 */
async ({ definitionId, locale }: { definitionId?: string, locale?: string } = {}): Promise<FormDefinitionLocaleRead> => {
        const response = await __transport.axios.get(`/form/definitions/${definitionId}/locales/${locale}`)
        return ApiResponse.create(response.data, response)},
      put: /**
 * Put Form Definition Locale
 *
 * Upsert a typed presentation overlay and set publication state.
 *
 * @endpoint PUT /form/definitions/{definition_id}/locales/{locale}
 */
async ({ definitionId, locale, formDefinitionLocaleWrite }: { definitionId?: string, locale?: string, formDefinitionLocaleWrite?: FormDefinitionLocaleWrite } = {}): Promise<FormDefinitionLocaleRead> => {
        const response = await __transport.axios.put(`/form/definitions/${definitionId}/locales/${locale}`, formDefinitionLocaleWrite)
        return ApiResponse.create(response.data, response)}
    },
    dispatch: /**
 * Dispatch Form Definition
 *
 * Validate and dispatch without exposing destination details to the browser.
 *
 * @endpoint POST /form/definitions/{definition_id}/dispatch
 */
async ({ definitionId, formDispatchRequest }: { definitionId?: string, formDispatchRequest?: FormDispatchRequest } = {}): Promise<FormDeliveryReceipt> => {
        const response = await __transport.axios.post(`/form/definitions/${definitionId}/dispatch`, formDispatchRequest)
        return ApiResponse.create(response.data, response)},
    resolveNext: /**
 * Resolve Next Step
 *
 * Optional, non-authoritative navigation helper for multi-step forms.
 * 
 * Given the current step key and the accumulated answers, returns the next
 * step key using the same first-match SCIM logic the client uses. A null
 * ``next_key`` (``terminal=true``) means the current step is a leaf and the
 * form is complete. This never gates submission — the client owns navigation.
 *
 * @endpoint POST /form/definitions/{definition_id}/resolve-next
 */
async ({ definitionId, resolveNextRequest }: { definitionId?: string, resolveNextRequest?: ResolveNextRequest } = {}): Promise<ResolveNextResponse> => {
        const response = await __transport.axios.post(`/form/definitions/${definitionId}/resolve-next`, resolveNextRequest)
        return ApiResponse.create(response.data, response)}
  },
  public_: {
    definitions: {
      get: /**
 * Get Public Form Definition
 *
 * Get an active definition explicitly published for public rendering.
 *
 * @endpoint GET /form/public/definitions/{definition_id}
 */
async ({ definitionId, locale }: { definitionId?: string, locale?: string } = {}): Promise<FormDefinitionRead> => {
        const response = await __transport.axios.get(`/form/public/definitions/${definitionId}`, { params: {locale} })
        return ApiResponse.create(response.data, response)},
      byName: /**
 * Get Public Form Definition By Name
 *
 * Get a public active definition by stable name.
 *
 * @endpoint GET /form/public/definitions/by-name/{name}
 */
async ({ name, locale }: { name?: string, locale?: string } = {}): Promise<FormDefinitionRead> => {
        const response = await __transport.axios.get(`/form/public/definitions/by-name/${name}`, { params: {locale} })
        return ApiResponse.create(response.data, response)},
      dispatch: /**
 * Dispatch Public Form Definition
 *
 * Dispatch an active, explicitly public form definition.
 * 
 * Anonymous callers cannot attribute the delivery to a person or attach it to
 * an existing submission — those are honoured only for a logged-in caller.
 *
 * @endpoint POST /form/public/definitions/{definition_id}/dispatch
 */
async ({ definitionId, formDispatchRequest }: { definitionId?: string, formDispatchRequest?: FormDispatchRequest } = {}): Promise<FormDeliveryReceipt> => {
        const response = await __transport.axios.post(`/form/public/definitions/${definitionId}/dispatch`, formDispatchRequest)
        return ApiResponse.create(response.data, response)}
    }
  },
  validate: /**
 * Validate Form Data
 *
 * Validate form data against a form definition schema.
 *
 * @endpoint POST /form/validate
 */
async ({ validateFormRequest }: { validateFormRequest?: ValidateFormRequest } = {}): Promise<ValidateFormResponse> => {
        const response = await __transport.axios.post('/form/validate', validateFormRequest)
        return ApiResponse.create(response.data, response)},
  analytics: {
    count: /**
 * Get Submission Count
 *
 * Get count of form submissions.
 *
 * @endpoint GET /form/analytics/count
 */
async ({ _formName, status }: { _formName?: string, status?: FormSubStatus } = {}): Promise<{ [key: string]: any }> => {
        const response = await __transport.axios.get('/form/analytics/count', { params: {'form_name': _formName, status} })
        return response.data},
    conversionRate: /**
 * Get Conversion Rate
 *
 * Get conversion rate for a form.
 *
 * @endpoint GET /form/analytics/conversion-rate/{form_name}
 */
async ({ formName }: { formName?: string } = {}): Promise<{ [key: string]: any }> => {
        const response = await __transport.axios.get(`/form/analytics/conversion-rate/${formName}`)
        return response.data},
    utmBreakdown: /**
 * Get Utm Breakdown
 *
 * Get breakdown of submissions by UTM source.
 *
 * @endpoint GET /form/analytics/utm-breakdown
 */
async ({ _formName }: { _formName?: string } = {}): Promise<{ [key: string]: any }> => {
        const response = await __transport.axios.get('/form/analytics/utm-breakdown', { params: {'form_name': _formName} })
        return response.data}
  }
};

/**
 * API functions for newsletter endpoints
 */
const newsletter = {
  send: /**
 * Send Transactional Email
 *
 * Send a transactional email (template_key + context, or subject + body_html).
 *
 * @endpoint POST /newsletter/send
 */
async (): Promise<{ [key: string]: any }> => {
        const response = await __transport.axios.post('/newsletter/send', {})
        return response.data},
  templates: {
    getByTemplateId: /**
 * Get Template
 *
 * Get a template by ID.
 *
 * @endpoint GET /newsletter/templates/{template_id}
 */
async ({ templateId }: { templateId?: string } = {}): Promise<EmailTemplateResponse> => {
        const response = await __transport.axios.get(`/newsletter/templates/${templateId}`)
        return ApiResponse.create(response.data, response)},
    put: /**
 * Update Template
 *
 * Update an email template.
 *
 * @endpoint PUT /newsletter/templates/{template_id}
 */
async ({ templateId, emailTemplateUpdate }: { templateId?: string, emailTemplateUpdate?: EmailTemplateUpdate } = {}): Promise<EmailTemplateResponse> => {
        const response = await __transport.axios.put(`/newsletter/templates/${templateId}`, emailTemplateUpdate)
        return ApiResponse.create(response.data, response)},
    delete: /**
 * Delete Template
 *
 * Delete a template.
 *
 * @endpoint DELETE /newsletter/templates/{template_id}
 */
async ({ templateId }: { templateId?: string } = {}) => {
        const response = await __transport.axios.delete(`/newsletter/templates/${templateId}`)
        return ApiResponse.create(response.data, response)},
    post: /**
 * Create Template
 *
 * Create a new email template.
 *
 * @endpoint POST /newsletter/templates
 */
async ({ emailTemplateCreate }: { emailTemplateCreate?: EmailTemplateCreate } = {}): Promise<EmailTemplateResponse> => {
        const response = await __transport.axios.post('/newsletter/templates', emailTemplateCreate)
        return ApiResponse.create(response.data, response)},
    get: /**
 * List Templates
 *
 * List email templates.
 *
 * @endpoint GET /newsletter/templates
 */
async ({ _templateType, _isActive, offset, limit = 100 }: { _templateType?: TemplateType, _isActive?: boolean, offset?: number, limit?: number } = {}): Promise<ApiResponse<EmailTemplateResponse[]>> => {
        const response = await __transport.axios.get('/newsletter/templates', { params: {'template_type': _templateType, 'is_active': _isActive, offset, limit} })
        return ApiResponse.create(response.data, response)},
    render: /**
 * Render Template Document
 *
 * Render an unsaved canonical document for editor preview.
 *
 * @endpoint POST /newsletter/templates/_render
 */
async ({ emailDocumentRenderRequest }: { emailDocumentRenderRequest?: EmailDocumentRenderRequest } = {}): Promise<EmailDocumentRenderResponse> => {
        const response = await __transport.axios.post('/newsletter/templates/_render', emailDocumentRenderRequest)
        return ApiResponse.create(response.data, response)},
    byKey: /**
 * Get Template By Key
 *
 * Get a template by its unique key.
 *
 * @endpoint GET /newsletter/templates/by-key/{key}
 */
async ({ key }: { key?: string } = {}): Promise<EmailTemplateResponse> => {
        const response = await __transport.axios.get(`/newsletter/templates/by-key/${key}`)
        return ApiResponse.create(response.data, response)}
  },
  suppressions: {
    delete: /**
 * Unsuppress Email
 *
 * Remove a suppression (re-allow sending to this address).
 *
 * @endpoint DELETE /newsletter/suppressions/{email}
 */
async ({ email }: { email?: string } = {}) => {
        const response = await __transport.axios.delete(`/newsletter/suppressions/${email}`)
        return ApiResponse.create(response.data, response)},
    get: /**
 * List Suppressions
 *
 * List globally suppressed email addresses (newest first).
 *
 * @endpoint GET /newsletter/suppressions
 */
async ({ offset, limit = 100 }: { offset?: number, limit?: number } = {}): Promise<ApiResponse<EmailSuppressionResponse[]>> => {
        const response = await __transport.axios.get('/newsletter/suppressions', { params: {offset, limit} })
        return ApiResponse.create(response.data, response)},
    post: /**
 * Suppress Email
 *
 * Manually suppress an email address (do-not-email).
 *
 * @endpoint POST /newsletter/suppressions
 */
async ({ suppressEmailRequest }: { suppressEmailRequest?: SuppressEmailRequest } = {}): Promise<EmailSuppressionResponse> => {
        const response = await __transport.axios.post('/newsletter/suppressions', suppressEmailRequest)
        return ApiResponse.create(response.data, response)}
  },
  campaigns: {
    getByCampaignId: /**
 * Get Campaign
 *
 * Get a campaign by ID.
 *
 * @endpoint GET /newsletter/campaigns/{campaign_id}
 */
async ({ campaignId }: { campaignId?: string } = {}): Promise<CampaignResponse> => {
        const response = await __transport.axios.get(`/newsletter/campaigns/${campaignId}`)
        return ApiResponse.create(response.data, response)},
    put: /**
 * Update Campaign
 *
 * Update a campaign.
 *
 * @endpoint PUT /newsletter/campaigns/{campaign_id}
 */
async ({ campaignId, campaignUpdate }: { campaignId?: string, campaignUpdate?: CampaignUpdate } = {}): Promise<CampaignResponse> => {
        const response = await __transport.axios.put(`/newsletter/campaigns/${campaignId}`, campaignUpdate)
        return ApiResponse.create(response.data, response)},
    delete: /**
 * Delete Campaign
 *
 * Delete a campaign.
 *
 * @endpoint DELETE /newsletter/campaigns/{campaign_id}
 */
async ({ campaignId }: { campaignId?: string } = {}) => {
        const response = await __transport.axios.delete(`/newsletter/campaigns/${campaignId}`)
        return ApiResponse.create(response.data, response)},
    post: /**
 * Create Campaign
 *
 * Create a new campaign targeting a segment.
 *
 * @endpoint POST /newsletter/campaigns
 */
async ({ campaignCreate }: { campaignCreate?: CampaignCreate } = {}): Promise<CampaignResponse> => {
        const response = await __transport.axios.post('/newsletter/campaigns', campaignCreate)
        return ApiResponse.create(response.data, response)},
    get: /**
 * List Campaigns
 *
 * List campaigns.
 *
 * @endpoint GET /newsletter/campaigns
 */
async ({ _campaignStatus, offset, limit = 100 }: { _campaignStatus?: CampaignStatus, offset?: number, limit?: number } = {}): Promise<ApiResponse<CampaignResponse[]>> => {
        const response = await __transport.axios.get('/newsletter/campaigns', { params: {'campaign_status': _campaignStatus, offset, limit} })
        return ApiResponse.create(response.data, response)},
    stats: /**
 * Get Campaign Stats
 *
 * Get campaign statistics.
 *
 * @endpoint GET /newsletter/campaigns/{campaign_id}/stats
 */
async ({ campaignId }: { campaignId?: string } = {}): Promise<CampaignStatsResponse> => {
        const response = await __transport.axios.get(`/newsletter/campaigns/${campaignId}/stats`)
        return ApiResponse.create(response.data, response)},
    schedule: /**
 * Schedule Campaign
 *
 * Schedule a campaign for sending.
 *
 * @endpoint POST /newsletter/campaigns/{campaign_id}/schedule
 */
async ({ campaignId }: { campaignId?: string } = {}): Promise<CampaignResponse> => {
        const response = await __transport.axios.post(`/newsletter/campaigns/${campaignId}/schedule`, {})
        return ApiResponse.create(response.data, response)},
    send: /**
 * Send Campaign
 *
 * Send a campaign to its segment audience (suppression-filtered).
 *
 * @endpoint POST /newsletter/campaigns/{campaign_id}/send
 */
async ({ campaignId }: { campaignId?: string } = {}): Promise<{ [key: string]: any }> => {
        const response = await __transport.axios.post(`/newsletter/campaigns/${campaignId}/send`, {})
        return response.data},
    test: /**
 * Send Test Email
 *
 * Send a one-off test email to a single address (action only, no record).
 *
 * @endpoint POST /newsletter/campaigns/test
 */
async ({ sendTestEmailRequest }: { sendTestEmailRequest?: SendTestEmailRequest } = {}): Promise<SendTestEmailResponse> => {
        const response = await __transport.axios.post('/newsletter/campaigns/test', sendTestEmailRequest)
        return ApiResponse.create(response.data, response)}
  },
  p: /**
 * Track Email Open
 *
 * Track email open via transparent 1x1 pixel.
 *
 * @endpoint GET /newsletter/p/{token}
 */
async ({ token }: { token?: string } = {}) => {
        const response = await __transport.axios.get(`/newsletter/p/${token}`)
        return ApiResponse.create(response.data, response)},
  r: /**
 * Track Link Click
 *
 * Track link click and redirect to the original URL.
 *
 * @endpoint GET /newsletter/r/{token}
 */
async ({ token, url }: { token?: string, url?: string } = {}) => {
        const response = await __transport.axios.get(`/newsletter/r/${token}`, { params: {url} })
        return ApiResponse.create(response.data, response)},
  unsubscribe: {
    post: /**
 * Unsubscribe
 *
 * Unsubscribe (API): suppress the email carried by a signed token.
 *
 * @endpoint POST /newsletter/unsubscribe
 */
async ({ unsubscribeRequest }: { unsubscribeRequest?: UnsubscribeRequest } = {}): Promise<EmailSuppressionResponse> => {
        const response = await __transport.axios.post('/newsletter/unsubscribe', unsubscribeRequest)
        return ApiResponse.create(response.data, response)},
    get: /**
 * Unsubscribe Page
 *
 * Show the unsubscribe confirmation page.
 *
 * @endpoint GET /newsletter/unsubscribe
 */
async ({ token }: { token?: string } = {}) => {
        const response = await __transport.axios.get('/newsletter/unsubscribe', { params: {token} })
        return ApiResponse.create(response.data, response)},
    confirm: /**
 * Unsubscribe Confirm
 *
 * Handle the unsubscribe form submission (token-based, global suppression).
 *
 * @endpoint POST /newsletter/unsubscribe/confirm
 */
async () => {
        const response = await __transport.axios.post('/newsletter/unsubscribe/confirm', {})
        return ApiResponse.create(response.data, response)}
  }
};

/**
 * API functions for files endpoints
 */
const files = {
  getByPathKey: /**
 * Get File
 *
 * Get file by path key.
 * 
 * Example:
 *     GET /files/customer-123/profile.jpg
 *
 * @endpoint GET /files/{path_key}
 */
async ({ pathKey }: { pathKey?: string } = {}): Promise<FileResponse> => {
        const response = await __transport.axios.get(`/files/${pathKey}`)
        return ApiResponse.create(response.data, response)},
  delete: /**
 * Delete File
 *
 * Delete file.
 * 
 * Example:
 *     DELETE /files/customer-123/profile.jpg
 *
 * @endpoint DELETE /files/{path_key}
 */
async ({ pathKey }: { pathKey?: string } = {}) => {
        const response = await __transport.axios.delete(`/files/${pathKey}`)
        return ApiResponse.create(response.data, response)},
  patch: /**
 * Update File
 *
 * Update file - tags and/or meta.
 * 
 * Examples:
 *     # Add tags
 *     PATCH /files/customer-123/file.pdf?add_tags=verified&add_tags=important
 * 
 *     # Set tags
 *     PATCH /files/customer-123/file.pdf?set_tags=contract&set_tags=final
 * 
 *     # Update meta
 *     PATCH /files/customer-123/file.pdf
 *     Body: {author: "John Doe", department: "Sales"}
 * 
 *     # Update both
 *     PATCH /files/customer-123/file.pdf?add_tags=verified
 *     Body: {author: "John Doe"}
 * 
 * Query Parameters:
 *     - add_tags: Tags to add (can specify multiple)
 *     - remove_tags: Tags to remove (can specify multiple)
 *     - set_tags: Set to these tags (replaces all)
 * 
 * Body:
 *     - meta: Dict to merge with existing meta
 *
 * @endpoint PATCH /files/{path_key}
 */
async ({ pathKey, _addTags, _removeTags, _setTags, meta }: { pathKey?: string, _addTags?: string[], _removeTags?: string[], _setTags?: string[], meta?: { [key: string]: any } } = {}): Promise<FileResponse> => {
        const response = await __transport.axios.patch(`/files/${pathKey}`, meta, { params: {'add_tags': _addTags, 'remove_tags': _removeTags, 'set_tags': _setTags} })
        return ApiResponse.create(response.data, response)},
  get: /**
 * Query Files Endpoint
 *
 * Query files with filters - POWERFUL!
 * 
 * Examples:
 *     # All files for customer
 *     GET /files?ref_id=customer-123
 * 
 *     # Customer's contracts
 *     GET /files?ref_id=customer-123&has_tag=contract
 * 
 *     # Signed contracts from 2024
 *     GET /files?has_all_tags=contract&has_all_tags=signed&has_all_tags=2024
 * 
 *     # Files in directory
 *     GET /files?ref_id=customer-123&dir_path=deals/deal-456
 * 
 * Query Parameters:
 *     - ref_id: Filter by entity reference
 *     - dir_path: Filter by directory (prefix match)
 *     - has_tag: Has this tag
 *     - has_any_tags: Has any of these tags (can specify multiple)
 *     - has_all_tags: Has all of these tags (can specify multiple)
 *     - offset: Pagination offset
 *     - limit: Max results (1-1000)
 *
 * @endpoint GET /files
 */
async ({ _refId, _dirPath, _hasTag, _hasAnyTags, _hasAllTags, offset, limit = 100 }: { _refId?: string, _dirPath?: string, _hasTag?: string, _hasAnyTags?: string[], _hasAllTags?: string[], offset?: number, limit?: number } = {}): Promise<ApiResponse<FileResponse[]>> => {
        const response = await __transport.axios.get('/files', { params: {'ref_id': _refId, 'dir_path': _dirPath, 'has_tag': _hasTag, 'has_any_tags': _hasAnyTags, 'has_all_tags': _hasAllTags, offset, limit} })
        return ApiResponse.create(response.data, response)},
  content: /**
 * Download File
 *
 * Return stored file bytes through the application.
 *
 * @endpoint GET /files/content/{path_key}
 */
async ({ pathKey }: { pathKey?: string } = {}) => {
        const response = await __transport.axios.get(`/files/content/${pathKey}`)
        return ApiResponse.create(response.data, response)},
  accessToken: {
    delete: /**
 * Revoke Access Token
 *
 * Revoke a grant so its token immediately stops working.
 *
 * @endpoint DELETE /files/access-token/{grant_id}
 */
async ({ grantId }: { grantId?: string } = {}) => {
        const response = await __transport.axios.delete(`/files/access-token/${grantId}`)
        return ApiResponse.create(response.data, response)},
    post: /**
 * Issue Access Token
 *
 * Mint a revocable, time-limited token to read a single file.
 * 
 * Requires read access to ``path_key`` (tenant/owner scope enforced); the
 * returned token can be used on ``GET /files/download/{path_key}``.
 *
 * @endpoint POST /files/access-token
 */
async ({ grantTokenRequest }: { grantTokenRequest?: GrantTokenRequest } = {}): Promise<FileGrantToken> => {
        const response = await __transport.axios.post('/files/access-token', grantTokenRequest)
        return ApiResponse.create(response.data, response)}
  },
  download: /**
 * Token Download
 *
 * Stream file bytes authorized by a signed, revocable grant token.
 * 
 * Accepts the token as ``?token=`` or ``Authorization: Bearer <token>``.
 * This route is a public door (see ``access.py``); the grant is the authority.
 *
 * @endpoint GET /files/download/{path_key}
 */
async ({ pathKey, token }: { pathKey?: string, token?: string } = {}) => {
        const response = await __transport.axios.get(`/files/download/${pathKey}`, { params: {token} })
        return ApiResponse.create(response.data, response)},
  image: /**
 * Image Variant
 *
 * Serve a cropped/resized/re-encoded image, caching the derived variant.
 * 
 * ``crop`` is ``x,y,width,height`` (applied before resize). ``fmt`` defaults
 * to webp. Derived images are public and CDN-cacheable.
 *
 * @endpoint GET /files/image/{path_key}
 */
async ({ pathKey, w, h, fit = 'cover', fmt, q, crop }: { pathKey?: string, w?: number, h?: number, fit?: FitMode, fmt?: OutputFormat, q?: number, crop?: string } = {}) => {
        const response = await __transport.axios.get(`/files/image/${pathKey}`, { params: {w, h, fit, fmt, q, crop} })
        return ApiResponse.create(response.data, response)},
  upload: /**
 * Upload File
 *
 * Upload a file - SIMPLE!
 * 
 * Examples:
 *     POST /files/upload
 *         ?ref_id=customer-123
 *         &dir_path=deals/deal-456
 *         &tags=contract
 *         &tags=signed
 * 
 * Query Parameters:
 *     - ref_id: Link to entity (customer ID, deal ID, etc.)
 *     - dir_path: Directory within ref_id
 *     - tags: Tags (can specify multiple)
 *     - metadata: JSON metadata
 *     - override_existing: Override if file exists
 *     - private: Store with a private ACL (no public CDN access); the
 *       response carries a short-lived ``download_url`` instead.
 *
 * @endpoint POST /files/upload
 */
async ({ formData }: { formData?: FormData } = {}): Promise<FileResponse> => {
        const response = await __transport.axios.post('/files/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        })
        return ApiResponse.create(response.data, response)},
  anonymous: /**
 * Delete Anonymous File
 *
 * Delete one anonymous upload using its path-bound capability token.
 *
 * @endpoint DELETE /files/anonymous/{path_key}
 */
async ({ pathKey, token }: { pathKey?: string, token?: string } = {}) => {
        const response = await __transport.axios.delete(`/files/anonymous/${pathKey}`, { params: {token} })
        return ApiResponse.create(response.data, response)},
  attachments: {
    delete: /**
 * Remove Attachment
 *
 * Detach a file from a record (does NOT delete the file itself).
 *
 * @endpoint DELETE /files/attachments/{attachment_id}
 */
async ({ attachmentId }: { attachmentId?: string } = {}) => {
        const response = await __transport.axios.delete(`/files/attachments/${attachmentId}`)
        return ApiResponse.create(response.data, response)},
    post: /**
 * Create Attachment
 *
 * Attach a file to a domain record.
 *
 * @endpoint POST /files/attachments
 */
async ({ attachRequest }: { attachRequest?: AttachRequest } = {}): Promise<FileAttachmentRead> => {
        const response = await __transport.axios.post('/files/attachments', attachRequest)
        return ApiResponse.create(response.data, response)},
    get: /**
 * Query Attachments
 *
 * List file attachments for a record.
 *
 * @endpoint GET /files/attachments
 */
async ({ _recordId, _recordType, slot }: { _recordId?: string, _recordType?: string, slot?: string } = {}): Promise<ApiResponse<FileAttachmentRead[]>> => {
        const response = await __transport.axios.get('/files/attachments', { params: {'record_id': _recordId, 'record_type': _recordType, slot} })
        return ApiResponse.create(response.data, response)},
    batch: /**
 * Create Attachments Batch
 *
 * Attach multiple files to a domain record.
 *
 * @endpoint POST /files/attachments/batch
 */
async ({ attachManyRequest }: { attachManyRequest?: AttachManyRequest } = {}): Promise<ApiResponse<FileAttachmentRead[]>> => {
        const response = await __transport.axios.post('/files/attachments/batch', attachManyRequest)
        return ApiResponse.create(response.data, response)}
  }
};

/**
 * API functions for automations endpoints
 */
const automations = {
  getByAutomationId: /**
 * Get Automation
 *
 * Get a specific automation.
 *
 * @endpoint GET /automations/{automation_id}
 */
async ({ automationId }: { automationId?: string } = {}): Promise<AutomationResponse> => {
        const response = await __transport.axios.get(`/automations/${automationId}`)
        return ApiResponse.create(response.data, response)},
  put: /**
 * Update Automation
 *
 * Update an automation.
 *
 * @endpoint PUT /automations/{automation_id}
 */
async ({ automationId, automationUpdate }: { automationId?: string, automationUpdate?: AutomationUpdate } = {}): Promise<AutomationResponse> => {
        const response = await __transport.axios.put(`/automations/${automationId}`, automationUpdate)
        return ApiResponse.create(response.data, response)},
  delete: /**
 * Delete Automation
 *
 * Delete an automation.
 *
 * @endpoint DELETE /automations/{automation_id}
 */
async ({ automationId }: { automationId?: string } = {}) => {
        const response = await __transport.axios.delete(`/automations/${automationId}`)
        return ApiResponse.create(response.data, response)},
  get: /**
 * List Automations
 *
 * List all automations with optional filtering.
 *
 * @endpoint GET /automations
 */
async ({ _enabledOnly, _triggerEvent, _createdBy, search, offset, limit = 50 }: { _enabledOnly?: boolean, _triggerEvent?: string, _createdBy?: string, search?: string, offset?: number, limit?: number } = {}): Promise<ApiResponse<AutomationResponse[]>> => {
        const response = await __transport.axios.get('/automations', { params: {'enabled_only': _enabledOnly, 'trigger_event': _triggerEvent, 'created_by': _createdBy, search, offset, limit} })
        return ApiResponse.create(response.data, response)},
  post: /**
 * Create Automation
 *
 * Create a new automation.
 *
 * @endpoint POST /automations
 */
async ({ automationCreate }: { automationCreate?: AutomationCreate } = {}): Promise<AutomationResponse> => {
        const response = await __transport.axios.post('/automations', automationCreate)
        return ApiResponse.create(response.data, response)},
  palette: /**
 * Get Palette
 *
 * Return typed triggers, built-ins, providers, actions, policies, and UI hints.
 *
 * @endpoint GET /automations/palette
 */
async (): Promise<AutomationPalette> => {
        const response = await __transport.axios.get('/automations/palette')
        return ApiResponse.create(response.data, response)},
  invoke: /**
 * Invoke Automation
 *
 * Invoke an HTTP-exposed automation as the authenticated identity.
 *
 * @endpoint POST /automations/{automation_ref}/invoke
 */
async ({ automationRef, automationInvokeRequest }: { automationRef?: string, automationInvokeRequest?: AutomationInvokeRequest } = {}): Promise<AutomationExecutionResponse> => {
        const response = await __transport.axios.post(`/automations/${automationRef}/invoke`, automationInvokeRequest)
        return ApiResponse.create(response.data, response)},
  ai: /**
 * Ai Build Automation
 *
 * Build an automation from a natural-language description.
 * 
 * The AI either creates the automation directly or returns clarifying
 * questions.  For multi-turn flows, pass prior turns in ``history``.
 *
 * @endpoint POST /automations/ai
 */
async ({ buildAutomationRequest }: { buildAutomationRequest?: BuildAutomationRequest } = {}): Promise<BuilderResponse> => {
        const response = await __transport.axios.post('/automations/ai', buildAutomationRequest)
        return ApiResponse.create(response.data, response)},
  toggle: /**
 * Toggle Automation
 *
 * Toggle automation enabled/disabled.
 *
 * @endpoint PATCH /automations/{automation_id}/toggle
 */
async ({ automationId }: { automationId?: string } = {}): Promise<AutomationResponse> => {
        const response = await __transport.axios.patch(`/automations/${automationId}/toggle`, {})
        return ApiResponse.create(response.data, response)},
  executions: {
    getByExecutionId: /**
 * Get Execution
 *
 * Get a specific execution.
 *
 * @endpoint GET /automations/executions/{execution_id}
 */
async ({ executionId }: { executionId?: string } = {}): Promise<AutomationExecutionResponse> => {
        const response = await __transport.axios.get(`/automations/executions/${executionId}`)
        return ApiResponse.create(response.data, response)},
    get: /**
 * Get Automation Executions
 *
 * Get execution history for an automation.
 *
 * @endpoint GET /automations/{automation_id}/executions
 */
async ({ automationId, offset, limit = 50 }: { automationId?: string, offset?: number, limit?: number } = {}): Promise<ApiResponse<AutomationExecutionResponse[]>> => {
        const response = await __transport.axios.get(`/automations/${automationId}/executions`, { params: {offset, limit} })
        return ApiResponse.create(response.data, response)}
  },
  test: /**
 * Test Automation
 *
 * Test an automation with sample data.
 *
 * @endpoint POST /automations/{automation_id}/test
 */
async ({ automationId, testAutomationRequest }: { automationId?: string, testAutomationRequest?: TestAutomationRequest } = {}): Promise<TestAutomationResult> => {
        const response = await __transport.axios.post(`/automations/${automationId}/test`, testAutomationRequest)
        return ApiResponse.create(response.data, response)},
  operators: {
    all: /**
 * Get Operators
 *
 * Get all available condition operators.
 *
 * @endpoint GET /automations/operators/all
 */
async (): Promise<ApiResponse<OperatorInfo[]>> => {
        const response = await __transport.axios.get('/automations/operators/all')
        return ApiResponse.create(response.data, response)}
  },
  triggers: {
    all: /**
 * Get Triggers
 *
 * Get all triggerable events with their supported trigger_params.
 * 
 * Returns only events that have declared trigger_params.
 *
 * @endpoint GET /automations/triggers/all
 */
async (): Promise<ApiResponse<{ [key: string]: any }[]>> => {
        const response = await __transport.axios.get('/automations/triggers/all')
        return ApiResponse.create(response.data, response)}
  },
  stats: {
    overview: /**
 * Get Automation Stats
 *
 * Get automation statistics.
 *
 * @endpoint GET /automations/stats/overview
 */
async (): Promise<AutomationStats> => {
        const response = await __transport.axios.get('/automations/stats/overview')
        return ApiResponse.create(response.data, response)}
  }
};

/**
 * API functions for agents endpoints
 */
const agents = {
  getByAgentId: /**
 * Get Agent
 *
 * Get an agent by ID.
 *
 * @endpoint GET /agents/{agent_id}
 */
async ({ agentId }: { agentId?: string } = {}): Promise<AgentResponse> => {
        const response = await __transport.axios.get(`/agents/${agentId}`)
        return ApiResponse.create(response.data, response)},
  patch: /**
 * Update Agent
 *
 * Update an agent.
 *
 * @endpoint PATCH /agents/{agent_id}
 */
async ({ agentId, agentUpdate }: { agentId?: string, agentUpdate?: AgentUpdate } = {}): Promise<AgentResponse> => {
        const response = await __transport.axios.patch(`/agents/${agentId}`, agentUpdate)
        return ApiResponse.create(response.data, response)},
  delete: /**
 * Delete Agent
 *
 * Delete an agent.
 *
 * @endpoint DELETE /agents/{agent_id}
 */
async ({ agentId }: { agentId?: string } = {}) => {
        const response = await __transport.axios.delete(`/agents/${agentId}`)
        return ApiResponse.create(response.data, response)},
  post: /**
 * Create Agent
 *
 * Create a new agent.
 *
 * @endpoint POST /agents/
 */
async ({ agentCreate }: { agentCreate?: AgentCreate } = {}): Promise<AgentResponse> => {
        const response = await __transport.axios.post('/agents/', agentCreate)
        return ApiResponse.create(response.data, response)},
  get: /**
 * List Agents
 *
 * List agents with optional filtering.
 *
 * @endpoint GET /agents/
 */
async ({ status, skip, limit = 100 }: { status?: AgentStatus, skip?: number, limit?: number } = {}): Promise<ApiResponse<AgentResponse[]>> => {
        const response = await __transport.axios.get('/agents/', { params: {status, skip, limit} })
        return ApiResponse.create(response.data, response)},
  tools: {
    get: /**
 * List Tools
 *
 * List all available tools in the registry.
 *
 * @endpoint GET /agents/tools
 */
async (): Promise<ToolsListResponse> => {
        const response = await __transport.axios.get('/agents/tools')
        return ApiResponse.create(response.data, response)},
    display: /**
 * List Tool Display
 *
 * Chat-facing tool metadata: name, icon, confirmation flag.
 * 
 * For every agent user (``agent:read``). No descriptions or parameter
 * schemas — the full catalog (``GET /agents/tools``) stays admin-only.
 *
 * @endpoint GET /agents/tools/display
 */
async (): Promise<ToolsDisplayResponse> => {
        const response = await __transport.axios.get('/agents/tools/display')
        return ApiResponse.create(response.data, response)}
  },
  directory: /**
 * List Agent Directory
 *
 * Active agents a user can chat with (``agent:read``): id, name, description.
 * 
 * Configuration (prompt, model, tools, memory) stays behind ``agent:admin``.
 *
 * @endpoint GET /agents/directory
 */
async (): Promise<ApiResponse<AgentPublic[]>> => {
        const response = await __transport.axios.get('/agents/directory')
        return ApiResponse.create(response.data, response)},
  models: /**
 * List Models
 *
 * List all available models from all configured providers.
 *
 * @endpoint GET /agents/models
 */
async (): Promise<ApiResponse<ModelInfo[]>> => {
        const response = await __transport.axios.get('/agents/models')
        return ApiResponse.create(response.data, response)},
  skills: {
    getBySkillId: /**
 * Get Skill
 *
 * Get a skill by ID.
 *
 * @endpoint GET /agents/skills/{skill_id}
 */
async ({ skillId }: { skillId?: string } = {}): Promise<SkillResponse> => {
        const response = await __transport.axios.get(`/agents/skills/${skillId}`)
        return ApiResponse.create(response.data, response)},
    patch: /**
 * Update Skill
 *
 * Update a skill.
 *
 * @endpoint PATCH /agents/skills/{skill_id}
 */
async ({ skillId, skillUpdate }: { skillId?: string, skillUpdate?: SkillUpdate } = {}): Promise<SkillResponse> => {
        const response = await __transport.axios.patch(`/agents/skills/${skillId}`, skillUpdate)
        return ApiResponse.create(response.data, response)},
    delete: /**
 * Delete Skill
 *
 * Delete a skill.
 *
 * @endpoint DELETE /agents/skills/{skill_id}
 */
async ({ skillId }: { skillId?: string } = {}) => {
        const response = await __transport.axios.delete(`/agents/skills/${skillId}`)
        return ApiResponse.create(response.data, response)},
    post: /**
 * Create Skill
 *
 * Create a new skill.
 *
 * @endpoint POST /agents/skills
 */
async ({ skillCreate }: { skillCreate?: SkillCreate } = {}): Promise<SkillResponse> => {
        const response = await __transport.axios.post('/agents/skills', skillCreate)
        return ApiResponse.create(response.data, response)},
    get: /**
 * List Skills
 *
 * List skills.
 *
 * @endpoint GET /agents/skills
 */
async ({ _isActive, skip, limit = 100 }: { _isActive?: boolean, skip?: number, limit?: number } = {}): Promise<ApiResponse<SkillResponse[]>> => {
        const response = await __transport.axios.get('/agents/skills', { params: {'is_active': _isActive, skip, limit} })
        return ApiResponse.create(response.data, response)}
  },
  conversations: {
    getByConversationId: /**
 * Get Conversation
 *
 * Get a conversation.
 *
 * @endpoint GET /agents/conversations/{conversation_id}
 */
async ({ conversationId }: { conversationId?: string } = {}): Promise<AgentConversationResponse> => {
        const response = await __transport.axios.get(`/agents/conversations/${conversationId}`)
        return ApiResponse.create(response.data, response)},
    patch: /**
 * Update Conversation
 *
 * Update conversation settings.
 *
 * @endpoint PATCH /agents/conversations/{conversation_id}
 */
async ({ conversationId, conversationUpdate }: { conversationId?: string, conversationUpdate?: ConversationUpdate } = {}): Promise<AgentConversationResponse> => {
        const response = await __transport.axios.patch(`/agents/conversations/${conversationId}`, conversationUpdate)
        return ApiResponse.create(response.data, response)},
    delete: /**
 * Delete Conversation
 *
 * Delete a conversation and all its messages.
 *
 * @endpoint DELETE /agents/conversations/{conversation_id}
 */
async ({ conversationId }: { conversationId?: string } = {}) => {
        const response = await __transport.axios.delete(`/agents/conversations/${conversationId}`)
        return ApiResponse.create(response.data, response)},
    post: /**
 * Create Conversation
 *
 * Create a new conversation.
 *
 * @endpoint POST /agents/conversations
 */
async ({ conversationCreate }: { conversationCreate?: ConversationCreate } = {}): Promise<AgentConversationResponse> => {
        const response = await __transport.axios.post('/agents/conversations', conversationCreate)
        return ApiResponse.create(response.data, response)},
    get: /**
 * List Conversations
 *
 * List conversations with optional filtering.
 * 
 * Non-admin users can only see their own conversations.
 * 
 * Query parameters:
 * - user_id: Filter by user ID (admins only; ignored for regular users)
 * - agent_id: Filter by agent ID
 * - ref_id: Filter by loose reference (e.g. case_id, problem_id)
 * - skip: Number of records to skip (default: 0)
 * - limit: Maximum records to return (default: 100)
 *
 * @endpoint GET /agents/conversations
 */
async ({ _userId, _agentId, _refId, _includeArchived, skip, limit = 100 }: { _userId?: string, _agentId?: string, _refId?: string, _includeArchived?: boolean, skip?: number, limit?: number } = {}): Promise<ApiResponse<AgentConversationResponse[]>> => {
        const response = await __transport.axios.get('/agents/conversations', { params: {'user_id': _userId, 'agent_id': _agentId, 'ref_id': _refId, 'include_archived': _includeArchived, skip, limit} })
        return ApiResponse.create(response.data, response)},
    artifacts: /**
 * List Artifacts
 *
 * List artifacts belonging to a conversation.
 *
 * @endpoint GET /agents/conversations/{conversation_id}/artifacts
 */
async ({ conversationId, skip, limit = 50 }: { conversationId?: string, skip?: number, limit?: number } = {}): Promise<ApiResponse<ArtifactResponse[]>> => {
        const response = await __transport.axios.get(`/agents/conversations/${conversationId}/artifacts`, { params: {skip, limit} })
        return ApiResponse.create(response.data, response)},
    search: /**
 * Search Conversations
 *
 * Search conversations by title or message content.
 * 
 * Non-admin users can only search their own conversations.
 * 
 * Query parameters:
 * - q: Search query string (required, case-insensitive)
 * - ref_id: Filter by ref_id to reduce load (optional)
 * - user_id: Filter by user_id to reduce load (optional, admins only)
 * 
 * Searches both conversation titles and message content.
 *
 * @endpoint GET /agents/conversations/search
 */
async ({ q, _refId, _userId, _includeArchived, skip, limit = 100 }: { q?: string, _refId?: string, _userId?: string, _includeArchived?: boolean, skip?: number, limit?: number } = {}): Promise<ApiResponse<AgentConversationResponse[]>> => {
        const response = await __transport.axios.get('/agents/conversations/search', { params: {q, 'ref_id': _refId, 'user_id': _userId, 'include_archived': _includeArchived, skip, limit} })
        return ApiResponse.create(response.data, response)},
    assets: /**
 * Get Conversation Asset
 *
 * Deliver one protected asset after conversation and attachment checks.
 *
 * @endpoint GET /agents/conversations/{conversation_id}/assets/{asset_id}
 */
async ({ conversationId, assetId }: { conversationId?: string, assetId?: string } = {}) => {
        const response = await __transport.axios.get(`/agents/conversations/${conversationId}/assets/${assetId}`)
        return ApiResponse.create(response.data, response)},
    activeRun: /**
 * Get Active Run
 *
 * Return the currently-active run_id for a conversation, or null.
 * 
 * Used by the frontend to reconnect to a running SSE stream after a page
 * refresh (the run_id can be replayed against the stream endpoint).
 * Returns ``{run_id: str | None, running: bool}``.
 *
 * @endpoint GET /agents/conversations/{conversation_id}/active-run
 */
async ({ conversationId }: { conversationId?: string } = {}): Promise<{ [key: string]: any }> => {
        const response = await __transport.axios.get(`/agents/conversations/${conversationId}/active-run`)
        return response.data},
    queue: {
      put: /**
 * Update Queue Ingress
 *
 * Replace the text content of a queued message (and mark it ready).
 *
 * @endpoint PUT /agents/conversations/{conversation_id}/queue/{ingress_id}
 */
async ({ conversationId, ingressId, updateQueueEntryRequest }: { conversationId?: string, ingressId?: string, updateQueueEntryRequest?: UpdateQueueEntryRequest } = {}): Promise<QueueEntryResponse> => {
        const response = await __transport.axios.put(`/agents/conversations/${conversationId}/queue/${ingressId}`, updateQueueEntryRequest)
        return ApiResponse.create(response.data, response)},
      delete: /**
 * Delete Queue Ingress
 *
 * Remove a message from the queue (and its persisted user message).
 *
 * @endpoint DELETE /agents/conversations/{conversation_id}/queue/{ingress_id}
 */
async ({ conversationId, ingressId }: { conversationId?: string, ingressId?: string } = {}) => {
        const response = await __transport.axios.delete(`/agents/conversations/${conversationId}/queue/${ingressId}`)
        return ApiResponse.create(response.data, response)},
      get: /**
 * List Queue
 *
 * List all pending messages in a conversation's queue.
 *
 * @endpoint GET /agents/conversations/{conversation_id}/queue
 */
async ({ conversationId }: { conversationId?: string } = {}): Promise<QueueListResponse> => {
        const response = await __transport.axios.get(`/agents/conversations/${conversationId}/queue`)
        return ApiResponse.create(response.data, response)},
      editing: /**
 * Mark Queue Ingress Editing
 *
 * Mark a queue ingress as ``editing`` (holds the drain loop at this ingress).
 *
 * @endpoint POST /agents/conversations/{conversation_id}/queue/{ingress_id}/editing
 */
async ({ conversationId, ingressId }: { conversationId?: string, ingressId?: string } = {}): Promise<QueueEntryResponse> => {
        const response = await __transport.axios.post(`/agents/conversations/${conversationId}/queue/${ingressId}/editing`, {})
        return ApiResponse.create(response.data, response)},
      ready: /**
 * Mark Queue Ingress Ready
 *
 * Mark a queue ingress ``ready`` and restart the drain loop if idle.
 *
 * @endpoint POST /agents/conversations/{conversation_id}/queue/{ingress_id}/ready
 */
async ({ conversationId, ingressId }: { conversationId?: string, ingressId?: string } = {}): Promise<QueueEntryResponse> => {
        const response = await __transport.axios.post(`/agents/conversations/${conversationId}/queue/${ingressId}/ready`, {})
        return ApiResponse.create(response.data, response)}
    },
    messages: {
      get: /**
 * Get Conversation History
 *
 * Get conversation message history with pagination.
 * 
 * Default limit is 500 (was 100): long-running case conversations routinely
 * exceed 100 messages, and the service now returns the most RECENT ``limit``
 * messages, so a low limit hid a client's latest turns (looked like deleted
 * messages). 500 comfortably covers real conversations while the newest-window
 * logic bounds the worst case.
 *
 * @endpoint GET /agents/conversations/{conversation_id}/messages
 */
async ({ conversationId, limit = 500 }: { conversationId?: string, limit?: number } = {}): Promise<ConversationHistoryResponse> => {
        const response = await __transport.axios.get(`/agents/conversations/${conversationId}/messages`, { params: {limit} })
        return ApiResponse.create(response.data, response)},
      post: /**
 * Start an agent run (returns run_id for SSE stream)
 *
 * Send a message to the agent.  Returns immediately with a ``run_id``.
 * 
 * Open ``GET /conversations/{id}/messages/stream?run_id={run_id}`` to receive
 * the token stream as Server-Sent Events.  Both calls can be fired in parallel —
 * events are buffered server-side so no tokens are lost.
 *
 * @endpoint POST /agents/conversations/{conversation_id}/messages
 */
async ({ conversationId, _xLangsmithParentRunId, _xLangsmithSessionId, sendAgentMessageRequest }: { conversationId?: string, _xLangsmithParentRunId?: string, _xLangsmithSessionId?: string, sendAgentMessageRequest?: SendAgentMessageRequest } = {}): Promise<StartMessageRunResponse> => {
        const response = await __transport.axios.post(`/agents/conversations/${conversationId}/messages`, sendAgentMessageRequest, { params: {'X-Langsmith-Parent-Run-Id': _xLangsmithParentRunId, 'X-Langsmith-Session-Id': _xLangsmithSessionId} })
        return ApiResponse.create(response.data, response)},
      stream: {
        get: /**
 * Subscribe to agent run stream (SSE)
 *
 * Server-Sent Events stream for an agent run. Pass the run_id returned by POST /messages.
 * 
 * 1. **user_message_saved**: user_message_saved event
 * 2. **interlaced**: interlaced event
 * 3. **acknowledgement**: acknowledgement event
 * 4. **interrupt**: interrupt event
 * 5. **skill_activated**: skill_activated event
 * 6. **artifact**: artifact event
 * 7. **token**: token event
 * 8. **ui_action**: ui_action event
 * 9. **ui_widget**: ui_widget event
 * 10. **ui_ext**: ui_ext event
 * 11. **done**: done event
 * 12. **error**: error event
 *
 * @endpoint GET /agents/conversations/{conversation_id}/messages/stream
 */
{
  /**
   * Stream SSE events from this endpoint (returns StreamController)
   * Event types: "acknowledgement", "artifact", "done", "error", "interlaced", "interrupt", "skill_activated", "token", "ui_action", "ui_ext", "ui_widget", "user_message_saved"
   * 
   * @example
   * const stream = api.endpoint.stream(params)
   *   .on('acknowledgement', (data) => console.log(data))
   *   .on('artifact', (data) => console.log(data))
   *   .on('done', (data) => console.log(data))
   *   .on('error', (data) => console.log(data))
   *   .on('interlaced', (data) => console.log(data))
   *   .on('interrupt', (data) => console.log(data))
   *   .on('skill_activated', (data) => console.log(data))
   *   .on('token', (data) => console.log(data))
   *   .on('ui_action', (data) => console.log(data))
   *   .on('ui_ext', (data) => console.log(data))
   *   .on('ui_widget', (data) => console.log(data))
   *   .on('user_message_saved', (data) => console.log(data))
   *   .on('error', (err) => console.error(err))
   *   .on('complete', () => console.log('Done!'))
   * 
   * // Close stream when needed
   * stream.close()
   */
  stream: ({ conversationId, _runId, _lastEventID }: { conversationId?: string, _runId?: string, _lastEventID?: string } = {}, options?: SSEStreamOptions): StreamController<AgentsConversationsMessagesStreamStreamEvents> => {
    const __qs = new URLSearchParams()
    if (_runId) __qs.set('run_id', String(_runId))
    if (_lastEventID) __qs.set('Last-Event-ID', String(_lastEventID))
    const __qsSuffix = __qs.size ? "?" + __qs.toString() : ""
    const url = `/agents/conversations/${conversationId}/messages/stream` + __qsSuffix
    return __transport.createSSEStream<AgentsConversationsMessagesStreamStreamEvents>(url, options)
  },
  /**
   * Regular GET request (non-streaming)
   */
  get: async ({ conversationId, _runId, _lastEventID }: { conversationId?: string, _runId?: string, _lastEventID?: string } = {}): Promise<AxiosResponse<any>> => {
    return __transport.axios.get(`/agents/conversations/${conversationId}/messages/stream`, { params: {'run_id': _runId, 'Last-Event-ID': _lastEventID} })
  }
},
        post: /**
 * Send Message Stream
 *
 * Send a message and stream the response as Server-Sent Events.
 * 
 * ## Event Types
 * 
 * 1. **user_message_saved** — user message saved to DB (`message_id`)
 * 2. **skill_activated** — skill loaded (`skill_name`, `description`)
 * 3. **middleware** — middleware status (`middleware_type`, `action`, `details`)
 * 4. **reasoning** — thinking delta from reasoning models (`delta`, `message_id`)
 * 5. **token** — content delta (`delta`, `message_id`, `role`)
 * 6. **tool_call** — tool invoked (`call_id`, `tool_name`, `arguments`, `requires_approval`)
 * 7. **tool_result** — tool result (`call_id`, `tool_name`, `result`, `error`)
 * 8. **done** — stream complete (`reason`, `message_id`, `usage`)
 * 9. **error** — error occurred (`error.code`, `error.message`, `error.retryable`)
 * 
 * ## SSE Format
 * ```
 * event: <event_type>
 * data: <json_payload>
 * 
 * ```
 *
 * @endpoint POST /agents/conversations/{conversation_id}/messages/stream
 */
{
  /**
   * Stream SSE events from this endpoint (returns StreamController)
   * Event types: "done", "error", "middleware", "reasoning", "skill_activated", "token", "tool_call", "tool_result", "user_message_saved"
   * 
   * @example
   * const stream = api.endpoint.stream(params)
   *   .on('done', (data) => console.log(data))
   *   .on('error', (data) => console.log(data))
   *   .on('middleware', (data) => console.log(data))
   *   .on('reasoning', (data) => console.log(data))
   *   .on('skill_activated', (data) => console.log(data))
   *   .on('token', (data) => console.log(data))
   *   .on('tool_call', (data) => console.log(data))
   *   .on('tool_result', (data) => console.log(data))
   *   .on('user_message_saved', (data) => console.log(data))
   *   .on('error', (err) => console.error(err))
   *   .on('complete', () => console.log('Done!'))
   * 
   * // Close stream when needed
   * stream.close()
   */
  stream: ({ conversationId, _lastEventID, _xLangsmithParentRunId, _xLangsmithSessionId, sendAgentMessageRequest }: { conversationId?: string, _lastEventID?: string, _xLangsmithParentRunId?: string, _xLangsmithSessionId?: string, sendAgentMessageRequest?: SendAgentMessageRequest } = {}, options?: SSEStreamOptions): StreamController<AgentsConversationsMessagesStreamStreamEvents> => {
    return __transport.createSSEStreamPost<AgentsConversationsMessagesStreamStreamEvents>(`/agents/conversations/${conversationId}/messages/stream`, sendAgentMessageRequest, options)
  },
  /**
   * Regular POST request (non-streaming)
   */
  post: async ({ conversationId, _lastEventID, _xLangsmithParentRunId, _xLangsmithSessionId, sendAgentMessageRequest }: { conversationId?: string, _lastEventID?: string, _xLangsmithParentRunId?: string, _xLangsmithSessionId?: string, sendAgentMessageRequest?: SendAgentMessageRequest } = {}): Promise<AxiosResponse<any>> => {
    return __transport.axios.post(`/agents/conversations/${conversationId}/messages/stream`, sendAgentMessageRequest)
  }
}
      }
    },
    compress: /**
 * Compress Conversation Context
 *
 * Summarize old messages to free up context window space.
 * 
 * Compresses all but the ``keep_last_n`` most-recent messages into a rolling
 * summary stored on the conversation. Future messages will include the summary
 * as leading context instead of the raw history, allowing the conversation to
 * continue indefinitely without losing important context.
 *
 * @endpoint POST /agents/conversations/{conversation_id}/compress
 */
async ({ conversationId, request }: { conversationId?: string, request?: CompressContextRequest } = {}): Promise<CompressContextResponse> => {
        const response = await __transport.axios.post(`/agents/conversations/${conversationId}/compress`, request)
        return ApiResponse.create(response.data, response)},
    interrupts: /**
 * List Pending Interrupts
 *
 * List tool calls paused for human approval in this conversation.
 *
 * @endpoint GET /agents/conversations/{conversation_id}/interrupts
 */
async ({ conversationId }: { conversationId?: string } = {}): Promise<ApiResponse<PendingInterrupt[]>> => {
        const response = await __transport.axios.get(`/agents/conversations/${conversationId}/interrupts`)
        return ApiResponse.create(response.data, response)},
    resume: /**
 * Resume Interrupted Run
 *
 * Resume a paused run with a human decision (approve / edit / reject).
 * 
 * On ``approve`` the paused tool executes; on ``edit`` it executes with
 * ``edited_args``; on ``reject`` it is skipped with an optional ``reason``.
 * The run then continues to completion.
 *
 * @endpoint POST /agents/conversations/{conversation_id}/resume
 */
async ({ conversationId, resumeRunRequest }: { conversationId?: string, resumeRunRequest?: ResumeRunRequest } = {}): Promise<AgentMessageResponse> => {
        const response = await __transport.axios.post(`/agents/conversations/${conversationId}/resume`, resumeRunRequest)
        return ApiResponse.create(response.data, response)},
    scan: /**
 * Scan Conversation
 *
 * Cold-scan a conversation for quality problems; create auto_detected
 * feedback rows for each finding. The mining engine for the backlog.
 *
 * @endpoint POST /agents/conversations/{conversation_id}/scan
 */
async ({ conversationId }: { conversationId?: string } = {}): Promise<JudgeScanResult> => {
        const response = await __transport.axios.post(`/agents/conversations/${conversationId}/scan`, {})
        return ApiResponse.create(response.data, response)}
  },
  artifacts: {
    get: /**
 * Get Artifact
 *
 * Get a single artifact by ID.
 *
 * @endpoint GET /agents/artifacts/{artifact_id}
 */
async ({ artifactId }: { artifactId?: string } = {}): Promise<ArtifactResponse> => {
        const response = await __transport.axios.get(`/agents/artifacts/${artifactId}`)
        return ApiResponse.create(response.data, response)},
    delete: /**
 * Delete Artifact
 *
 * Delete an artifact.
 *
 * @endpoint DELETE /agents/artifacts/{artifact_id}
 */
async ({ artifactId }: { artifactId?: string } = {}) => {
        const response = await __transport.axios.delete(`/agents/artifacts/${artifactId}`)
        return ApiResponse.create(response.data, response)},
    rename: /**
 * Rename Artifact
 *
 * Rename an artifact.
 *
 * @endpoint PATCH /agents/artifacts/{artifact_id}/rename
 */
async ({ artifactId, artifactRename }: { artifactId?: string, artifactRename?: ArtifactRename } = {}): Promise<ArtifactResponse> => {
        const response = await __transport.axios.patch(`/agents/artifacts/${artifactId}/rename`, artifactRename)
        return ApiResponse.create(response.data, response)},
    source: /**
 * Update Artifact Source
 *
 * Replace artifact source data (user-side edit).
 *
 * @endpoint PUT /agents/artifacts/{artifact_id}/source
 */
async ({ artifactId, artifactSourceUpdate }: { artifactId?: string, artifactSourceUpdate?: ArtifactSourceUpdate } = {}): Promise<ArtifactResponse> => {
        const response = await __transport.axios.put(`/agents/artifacts/${artifactId}/source`, artifactSourceUpdate)
        return ApiResponse.create(response.data, response)},
    export_: {
      post: /**
 * Export Artifact
 *
 * Export artifact to a downloadable file (docx/xlsx/pdf).
 * 
 * NOTE: sync ``def`` (not ``async def``) so FastAPI runs it in a thread pool,
 * which has no running event loop — required because ``_upload`` uses
 * ``asyncio.run()`` internally.
 *
 * @endpoint POST /agents/artifacts/{artifact_id}/export
 */
async ({ artifactId, fmt = 'docx' }: { artifactId?: string, fmt?: string } = {}): Promise<ArtifactResponse> => {
        const response = await __transport.axios.post(`/agents/artifacts/${artifactId}/export`, {}, { params: {fmt} })
        return ApiResponse.create(response.data, response)},
      get: /**
 * Download Artifact Export
 *
 * Deliver a protected export after conversation authorization.
 *
 * @endpoint GET /agents/artifacts/{artifact_id}/export
 */
async ({ artifactId }: { artifactId?: string } = {}) => {
        const response = await __transport.axios.get(`/agents/artifacts/${artifactId}/export`)
        return ApiResponse.create(response.data, response)}
    }
  },
  feedback: {
    getByFeedbackId: /**
 * Get Feedback
 *
 * @endpoint GET /agents/feedback/{feedback_id}
 */
async ({ feedbackId }: { feedbackId?: string } = {}): Promise<AgentFeedbackRead> => {
        const response = await __transport.axios.get(`/agents/feedback/${feedbackId}`)
        return ApiResponse.create(response.data, response)},
    patch: /**
 * Update Feedback
 *
 * Triage / edit a feedback item (assign layer, status, repro point).
 *
 * @endpoint PATCH /agents/feedback/{feedback_id}
 */
async ({ feedbackId, feedbackUpdate }: { feedbackId?: string, feedbackUpdate?: FeedbackUpdate } = {}): Promise<AgentFeedbackRead> => {
        const response = await __transport.axios.patch(`/agents/feedback/${feedbackId}`, feedbackUpdate)
        return ApiResponse.create(response.data, response)},
    delete: /**
 * Delete Feedback
 *
 * @endpoint DELETE /agents/feedback/{feedback_id}
 */
async ({ feedbackId }: { feedbackId?: string } = {}) => {
        const response = await __transport.axios.delete(`/agents/feedback/${feedbackId}`)
        return ApiResponse.create(response.data, response)},
    post: /**
 * Create Feedback
 *
 * Log a piece of conversation feedback (customer / QA / auto-detected).
 *
 * @endpoint POST /agents/feedback
 */
async ({ feedbackCreate }: { feedbackCreate?: FeedbackCreate } = {}): Promise<AgentFeedbackRead> => {
        const response = await __transport.axios.post('/agents/feedback', feedbackCreate)
        return ApiResponse.create(response.data, response)},
    get: /**
 * List Feedback
 *
 * List feedback with optional filters.
 *
 * @endpoint GET /agents/feedback
 */
async ({ _agentId, source, layer, status, offset, limit = 100 }: { _agentId?: string, source?: FeedbackSource, layer?: FeedbackLayer, status?: FeedbackStatus, offset?: number, limit?: number } = {}): Promise<ApiResponse<AgentFeedbackRead[]>> => {
        const response = await __transport.axios.get('/agents/feedback', { params: {'agent_id': _agentId, source, layer, status, offset, limit} })
        return ApiResponse.create(response.data, response)},
    aggregate: /**
 * Aggregate Feedback
 *
 * Roll up feedback counts grouped by layer | status | source.
 *
 * @endpoint GET /agents/feedback/aggregate
 */
async ({ by = 'layer', _agentId }: { by?: string, _agentId?: string } = {}): Promise<ApiResponse<FeedbackAggregateRow[]>> => {
        const response = await __transport.axios.get('/agents/feedback/aggregate', { params: {by, 'agent_id': _agentId} })
        return ApiResponse.create(response.data, response)},
    reproduce: /**
 * Reproduce Feedback
 *
 * Replay the conversation prefix to confirm the issue still occurs.
 *
 * @endpoint POST /agents/feedback/{feedback_id}/reproduce
 */
async ({ feedbackId }: { feedbackId?: string } = {}): Promise<FeedbackReproResult> => {
        const response = await __transport.axios.post(`/agents/feedback/${feedbackId}/reproduce`, {})
        return ApiResponse.create(response.data, response)},
    resolve: /**
 * Resolve Feedback
 *
 * Judge-gated resolution: re-run the prefix; flip to RESOLVED only if the
 * attribution judge confirms the problem no longer occurs.
 *
 * @endpoint POST /agents/feedback/{feedback_id}/resolve
 */
async ({ feedbackId }: { feedbackId?: string } = {}): Promise<FeedbackReproResult> => {
        const response = await __transport.axios.post(`/agents/feedback/${feedbackId}/resolve`, {})
        return ApiResponse.create(response.data, response)},
    attribute: /**
 * Attribute Feedback
 *
 * Run the attribution judge on an existing feedback item: read the trace,
 * assign the failing layer + suggested fix, move to TRIAGED.
 *
 * @endpoint POST /agents/feedback/{feedback_id}/attribute
 */
async ({ feedbackId }: { feedbackId?: string } = {}): Promise<JudgeAttributeResult> => {
        const response = await __transport.axios.post(`/agents/feedback/${feedbackId}/attribute`, {})
        return ApiResponse.create(response.data, response)}
  },
  capabilityConnectors: {
    getByConnectorId: /**
 * Get Connector
 *
 * Get a capability connector by ID.
 *
 * @endpoint GET /agents/capability-connectors/{connector_id}
 */
async ({ connectorId }: { connectorId?: string } = {}): Promise<CapabilityConnectorResponse> => {
        const response = await __transport.axios.get(`/agents/capability-connectors/${connectorId}`)
        return ApiResponse.create(response.data, response)},
    patch: /**
 * Update Connector
 *
 * Update a capability connector.
 *
 * @endpoint PATCH /agents/capability-connectors/{connector_id}
 */
async ({ connectorId, capabilityConnectorUpdate }: { connectorId?: string, capabilityConnectorUpdate?: CapabilityConnectorUpdate } = {}): Promise<CapabilityConnectorResponse> => {
        const response = await __transport.axios.patch(`/agents/capability-connectors/${connectorId}`, capabilityConnectorUpdate)
        return ApiResponse.create(response.data, response)},
    delete: /**
 * Delete Connector
 *
 * Delete a capability connector.
 *
 * @endpoint DELETE /agents/capability-connectors/{connector_id}
 */
async ({ connectorId }: { connectorId?: string } = {}) => {
        const response = await __transport.axios.delete(`/agents/capability-connectors/${connectorId}`)
        return ApiResponse.create(response.data, response)},
    post: /**
 * Create Connector
 *
 * Create a new capability connector.
 *
 * @endpoint POST /agents/capability-connectors
 */
async ({ capabilityConnectorCreate }: { capabilityConnectorCreate?: CapabilityConnectorCreate } = {}): Promise<CapabilityConnectorResponse> => {
        const response = await __transport.axios.post('/agents/capability-connectors', capabilityConnectorCreate)
        return ApiResponse.create(response.data, response)},
    get: /**
 * List Connectors
 *
 * List all attachable capabilities — external connectors AND in-process
 * capabilities. Both are referenceable by ``name`` in ``agent.capabilities``.
 * Filter with ``?source=connector`` or ``?source=builtin``.
 *
 * @endpoint GET /agents/capability-connectors
 */
async ({ _isActive, skip, limit = 100, source }: { _isActive?: boolean, skip?: number, limit?: number, source?: string } = {}): Promise<ApiResponse<CapabilityItem[]>> => {
        const response = await __transport.axios.get('/agents/capability-connectors', { params: {'is_active': _isActive, skip, limit, source} })
        return ApiResponse.create(response.data, response)},
    sync: /**
 * Sync Connector
 *
 * Re-fetch the tool listing from the remote source.
 * 
 * Use when the remote source has added, removed, or changed tools.
 * The cached ``tool_catalog`` is updated in place.
 *
 * @endpoint POST /agents/capability-connectors/{connector_id}/sync
 */
async ({ connectorId }: { connectorId?: string } = {}): Promise<CapabilityConnectorResponse> => {
        const response = await __transport.axios.post(`/agents/capability-connectors/${connectorId}/sync`, {})
        return ApiResponse.create(response.data, response)},
    oauth: {
      authorize: /**
 * Authorize Connector
 *
 * Start an OAuth flow for a connector. Returns the provider's authorize URL.
 *
 * @endpoint POST /agents/capability-connectors/{connector_id}/oauth/authorize
 */
async ({ connectorId }: { connectorId?: string } = {}): Promise<OAuthAuthorizeResponse> => {
        const response = await __transport.axios.post(`/agents/capability-connectors/${connectorId}/oauth/authorize`, {})
        return ApiResponse.create(response.data, response)},
      callback: /**
 * Oauth Callback
 *
 * Provider redirect target: exchange the code and store the token.
 *
 * @endpoint GET /agents/capability-connectors/oauth/callback
 */
async ({ code, state }: { code?: string, state?: string } = {}) => {
        const response = await __transport.axios.get('/agents/capability-connectors/oauth/callback', { params: {code, state} })
        return ApiResponse.create(response.data, response)}
    }
  },
  capabilities: {
    get: /**
 * Get Capability Detail
 *
 * Get a capability's detail and its tool list.
 * 
 * Resolves in-process (builtin) capabilities from the runtime registry and
 * remote (connector) capabilities from the DB, so the admin UI can render one
 * uniform detail page for both.
 *
 * @endpoint GET /agents/capabilities/{name}
 */
async ({ name }: { name?: string } = {}): Promise<CapabilityDetail> => {
        const response = await __transport.axios.get(`/agents/capabilities/${name}`)
        return ApiResponse.create(response.data, response)},
    add: /**
 * Add Agent Capabilities
 *
 * Connect capability connectors to an agent.
 *
 * @endpoint POST /agents/{agent_id}/capabilities/add
 */
async ({ agentId, agentCapabilitiesUpdate }: { agentId?: string, agentCapabilitiesUpdate?: AgentCapabilitiesUpdate } = {}): Promise<AgentResponse> => {
        const response = await __transport.axios.post(`/agents/${agentId}/capabilities/add`, agentCapabilitiesUpdate)
        return ApiResponse.create(response.data, response)},
    remove: /**
 * Remove Agent Capabilities
 *
 * Disconnect capability connectors from an agent.
 *
 * @endpoint POST /agents/{agent_id}/capabilities/remove
 */
async ({ agentId, agentCapabilitiesUpdate }: { agentId?: string, agentCapabilitiesUpdate?: AgentCapabilitiesUpdate } = {}): Promise<AgentResponse> => {
        const response = await __transport.axios.post(`/agents/${agentId}/capabilities/remove`, agentCapabilitiesUpdate)
        return ApiResponse.create(response.data, response)}
  },
  memory: {
    patch: /**
 * Update Memory
 *
 * Update agent memory file.
 *
 * @endpoint PATCH /agents/{agent_id}/memory/{file_path}
 */
async ({ agentId, filePath, memoryUpdate }: { agentId?: string, filePath?: string, memoryUpdate?: MemoryUpdate } = {}): Promise<MemoryResponse> => {
        const response = await __transport.axios.patch(`/agents/${agentId}/memory/${filePath}`, memoryUpdate)
        return ApiResponse.create(response.data, response)},
    delete: /**
 * Delete Memory
 *
 * Delete agent memory file.
 *
 * @endpoint DELETE /agents/{agent_id}/memory/{file_path}
 */
async ({ agentId, filePath }: { agentId?: string, filePath?: string } = {}) => {
        const response = await __transport.axios.delete(`/agents/${agentId}/memory/${filePath}`)
        return ApiResponse.create(response.data, response)},
    get: /**
 * List Memory
 *
 * List all memory files for an agent.
 *
 * @endpoint GET /agents/{agent_id}/memory
 */
async ({ agentId }: { agentId?: string } = {}): Promise<ApiResponse<MemoryResponse[]>> => {
        const response = await __transport.axios.get(`/agents/${agentId}/memory`)
        return ApiResponse.create(response.data, response)},
    post: /**
 * Create Memory
 *
 * Create agent memory file.
 *
 * @endpoint POST /agents/memory
 */
async ({ memoryCreate }: { memoryCreate?: MemoryCreate } = {}): Promise<MemoryResponse> => {
        const response = await __transport.axios.post('/agents/memory', memoryCreate)
        return ApiResponse.create(response.data, response)}
  },
  scenarios: {
    getByScenarioId: /**
 * Get Scenario
 *
 * Get a single scenario.
 *
 * @endpoint GET /agents/scenarios/{scenario_id}
 */
async ({ scenarioId }: { scenarioId?: string } = {}): Promise<ScenarioResponse> => {
        const response = await __transport.axios.get(`/agents/scenarios/${scenarioId}`)
        return ApiResponse.create(response.data, response)},
    patch: /**
 * Update Scenario
 *
 * Update a scenario.
 *
 * @endpoint PATCH /agents/scenarios/{scenario_id}
 */
async ({ scenarioId, scenarioUpdate }: { scenarioId?: string, scenarioUpdate?: ScenarioUpdate } = {}): Promise<ScenarioResponse> => {
        const response = await __transport.axios.patch(`/agents/scenarios/${scenarioId}`, scenarioUpdate)
        return ApiResponse.create(response.data, response)},
    delete: /**
 * Delete Scenario
 *
 * Delete a scenario.
 *
 * @endpoint DELETE /agents/scenarios/{scenario_id}
 */
async ({ scenarioId }: { scenarioId?: string } = {}) => {
        const response = await __transport.axios.delete(`/agents/scenarios/${scenarioId}`)
        return ApiResponse.create(response.data, response)},
    post: /**
 * Create Scenario
 *
 * Create a new scenario.
 *
 * @endpoint POST /agents/scenarios
 */
async ({ scenarioCreate }: { scenarioCreate?: ScenarioCreate } = {}): Promise<ScenarioResponse> => {
        const response = await __transport.axios.post('/agents/scenarios', scenarioCreate)
        return ApiResponse.create(response.data, response)},
    get: /**
 * List Scenarios
 *
 * List scenarios, optionally filtered by agent.
 *
 * @endpoint GET /agents/scenarios
 */
async ({ _agentId, skip, limit = 100 }: { _agentId?: string, skip?: number, limit?: number } = {}): Promise<ApiResponse<ScenarioResponse[]>> => {
        const response = await __transport.axios.get('/agents/scenarios', { params: {'agent_id': _agentId, skip, limit} })
        return ApiResponse.create(response.data, response)},
    messages: {
      getByMessageId: /**
 * Get Message
 *
 * Get a single message by ID.
 *
 * @endpoint GET /agents/scenarios/{scenario_id}/messages/{message_id}
 */
async ({ scenarioId, messageId }: { scenarioId?: string, messageId?: string } = {}): Promise<ScenarioMessageData> => {
        const response = await __transport.axios.get(`/agents/scenarios/${scenarioId}/messages/${messageId}`)
        return ApiResponse.create(response.data, response)},
      patch: /**
 * Update Message
 *
 * Partially update a message.
 *
 * @endpoint PATCH /agents/scenarios/{scenario_id}/messages/{message_id}
 */
async ({ scenarioId, messageId, scenarioMessageUpdate }: { scenarioId?: string, messageId?: string, scenarioMessageUpdate?: ScenarioMessageUpdate } = {}): Promise<ScenarioMessageData> => {
        const response = await __transport.axios.patch(`/agents/scenarios/${scenarioId}/messages/${messageId}`, scenarioMessageUpdate)
        return ApiResponse.create(response.data, response)},
      delete: /**
 * Delete Message
 *
 * Delete a message from a scenario.
 *
 * @endpoint DELETE /agents/scenarios/{scenario_id}/messages/{message_id}
 */
async ({ scenarioId, messageId }: { scenarioId?: string, messageId?: string } = {}) => {
        const response = await __transport.axios.delete(`/agents/scenarios/${scenarioId}/messages/${messageId}`)
        return ApiResponse.create(response.data, response)},
      post: /**
 * Add Message
 *
 * Append a message to a scenario.
 *
 * @endpoint POST /agents/scenarios/{scenario_id}/messages
 */
async ({ scenarioId, scenarioMessageData }: { scenarioId?: string, scenarioMessageData?: ScenarioMessageData } = {}): Promise<ScenarioMessageData> => {
        const response = await __transport.axios.post(`/agents/scenarios/${scenarioId}/messages`, scenarioMessageData)
        return ApiResponse.create(response.data, response)},
      get: /**
 * List Messages
 *
 * List all messages in a scenario.
 *
 * @endpoint GET /agents/scenarios/{scenario_id}/messages
 */
async ({ scenarioId }: { scenarioId?: string } = {}): Promise<ApiResponse<ScenarioMessageData[]>> => {
        const response = await __transport.axios.get(`/agents/scenarios/${scenarioId}/messages`)
        return ApiResponse.create(response.data, response)},
      run: /**
 * Run Message
 *
 * Generate an agent response for the given user message.
 * 
 * The agent receives the full conversation history up to this message as
 * context. Any existing assistant messages immediately following are
 * replaced. Returns the newly generated assistant message.
 *
 * @endpoint POST /agents/scenarios/{scenario_id}/messages/{message_id}/run
 */
async ({ scenarioId, messageId }: { scenarioId?: string, messageId?: string } = {}): Promise<ScenarioMessageData> => {
        const response = await __transport.axios.post(`/agents/scenarios/${scenarioId}/messages/${messageId}/run`, {})
        return ApiResponse.create(response.data, response)}
    }
  },
  conversationStarters: /**
 * Get Conversation Starters
 *
 * Return starter prompts applicable to the current caller.
 *
 * @endpoint GET /agents/{agent_id}/conversation-starters
 */
async ({ agentId, locale }: { agentId?: string, locale?: string } = {}): Promise<ApiResponse<AgentStarterPromptResponse[]>> => {
        const response = await __transport.axios.get(`/agents/${agentId}/conversation-starters`, { params: {locale} })
        return ApiResponse.create(response.data, response)}
};

/**
 * API functions for checkDb endpoints
 */
const checkDb = /**
 * Check Db
 *
 * @endpoint GET /check-db
 */
async (): Promise<{ [key: string]: any }> => {
        const response = await __transport.axios.get('/check-db')
        return response.data};

/**
 * API functions for project endpoints
 */
const project = {
  get: /**
 * Get Project Info
 *
 * Get project metadata and configuration.
 * 
 * Returns basic information about the current project including
 * name, version, environment, deployment type, and module counts.
 *
 * @endpoint GET /project/
 */
async (): Promise<{ [key: string]: any }> => {
        const response = await __transport.axios.get('/project/')
        return response.data},
  modules: /**
 * List Installed Modules
 *
 * List all installed modules with their metadata.
 * 
 * Returns detailed information about each registered module including:
 * - name, display_name, icon, color
 * - description, stability
 * - dependencies and extensions
 * - enabled status
 * - router prefix (if applicable)
 *
 * @endpoint GET /project/modules
 */
async (): Promise<ApiResponse<{ [key: string]: any }[]>> => {
        const response = await __transport.axios.get('/project/modules')
        return ApiResponse.create(response.data, response)},
  health: /**
 * Project Health Check
 *
 * Health check endpoint with module dependency validation.
 * 
 * Returns health status and checks if all module dependencies
 * are satisfied.
 *
 * @endpoint GET /project/health
 */
async (): Promise<{ [key: string]: any }> => {
        const response = await __transport.axios.get('/project/health')
        return response.data}
};

/**
 * Aggregated API object containing all endpoints
 */
const api = {
	events,
	person,
	authentication,
	admin,
	invitations,
	blox,
	bloxBuild,
	datastore,
	form,
	newsletter,
	files,
	automations,
	agents,
	checkDb,
	project,
};
return { api, events, person, authentication, admin, invitations, blox, bloxBuild, datastore, form, newsletter, files, automations, agents, checkDb, project }
}


const defaultBindings = createEndpointNamespaces(defaultTransport)
export const api = defaultBindings.api
export const events = defaultBindings.events
export const person = defaultBindings.person
export const authentication = defaultBindings.authentication
export const admin = defaultBindings.admin
export const invitations = defaultBindings.invitations
export const blox = defaultBindings.blox
export const bloxBuild = defaultBindings.bloxBuild
export const datastore = defaultBindings.datastore
export const form = defaultBindings.form
export const newsletter = defaultBindings.newsletter
export const files = defaultBindings.files
export const automations = defaultBindings.automations
export const agents = defaultBindings.agents
export const checkDb = defaultBindings.checkDb
export const project = defaultBindings.project
export const axios = defaultTransport.axios

/** Bind all generated endpoint namespaces to an isolated transport. */
export function createApiClient(transport: ApiTransport) {
	return createEndpointNamespaces(transport)
}

/** Resolve the default client's SSE base URL. */
export function getStreamBaseURL(): string {
	return defaultTransport.getStreamBaseURL()
}

// ============================================================================
// API Instance & Configuration
// ============================================================================

/**
 * API instance type with enhanced response objects
 */
type ApiInstance = typeof api & {
	$raw: typeof api
	$lastResponse: Ref<AxiosResponse | null>
	$axios: typeof axios
	$ApiResponse: typeof ApiResponse
	$config: Ref<ApiConfig | null>
}

// Global state
let apiInstance: ApiInstance | null = null

/**
 * Create and configure the API instance
 * @param config - API configuration options
 * @returns Configured API instance
 */
export function createApi(config: ApiConfig = {}): ApiInstance {
	configureApiTransport(defaultTransport, config)

	// API already returns ApiResponse directly!
	// Just add the special properties
	apiInstance = {
		...api,
		$raw: api,
		$lastResponse: defaultTransport.lastResponse,
		$axios: axios,
		$ApiResponse: ApiResponse,
		$config: defaultTransport.config
	} as ApiInstance

	return apiInstance
}

/**
 * Access the configured API instance
 * @returns API instance with enhanced response objects
 * @throws Error if createApi hasn't been called
 */
export function useApi(): ApiInstance {
	if (!apiInstance) {
		throw new Error(
			'API not initialized. Call createApi() first.\n\n' +
			'Example:\n' +
			'import { createApi } from \'./generated/api\'\n' +
			'createApi({ baseURL: \'/api\' })'
		)
	}
	return apiInstance
}

// ============================================================================
// Re-export utilities for convenience
// ============================================================================

export {
	parseApiError,
	formatFieldErrors,
	useApiRequest,
	useCancellableRequest,
	unwrap,
	ApiResponse,
	type ApiConfig,
	type ParsedError,
	type ResponseMetadata,
	type ApiRequestState,
	type FastAPIValidationError,
	type FastAPIErrorResponse,
	type RequestInterceptor,
	type ResponseInterceptor,
	type Unwrapped
}
